import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { X } from "lucide-react";
import { referenceDataAPI, pdfAPI } from "../services/api";
import {
  getUnifiedParameters,
  shouldHideParameterByMasterData,
} from "../utils/healthParameterNormalizer";

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.split("T")[0].split(" ")[0];
  let parts = null;

  if (dateOnly.includes("-")) {
    parts = dateOnly.split("-");
  } else if (dateOnly.includes("/")) {
    parts = dateOnly.split("/");
  }

  if (parts?.length === 3) {
    let year;
    let month;
    let day;

    if (parts[0].length === 4) {
      year = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      day = Number.parseInt(parts[2], 10);
    } else {
      day = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      year = Number.parseInt(parts[2], 10);
    }

    if (year > 2400) {
      year -= 543;
    } else if (year < 100) {
      year += 2000;
    }

    const parsed = new Date(year, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const calculateAgeAtDate = (birthday, referenceDate) => {
  const birthDate = parseFlexibleDate(birthday);
  const examDate = parseFlexibleDate(referenceDate);

  if (!birthDate || !examDate) {
    return null;
  }

  let age = examDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = examDate.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && examDate.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  if (age < 0 || age > 150) {
    return null;
  }

  return age;
};

const getRecordExamAge = (record) =>
  calculateAgeAtDate(
    record?.birthday || record?.birthdate || record?.birth_date,
    record?.rawDate || record?.dateadd || record?.date,
  );

// อายุแบบละเอียด (ทศนิยมของปี) สำหรับจับคู่ช่วงอายุใน medium.json ที่มีหน่วยเป็นวัน/เดือน (เช่น ทารกแรกเกิด)
const calculatePreciseAgeAtDate = (birthday, referenceDate) => {
  const birthDate = parseFlexibleDate(birthday);
  const examDate = parseFlexibleDate(referenceDate);

  if (!birthDate || !examDate) {
    return null;
  }

  const msPerDay = 24 * 60 * 60 * 1000;
  const ageInYears =
    (examDate.getTime() - birthDate.getTime()) / msPerDay / 365.25;

  if (ageInYears < 0 || ageInYears > 150) {
    return null;
  }

  return ageInYears;
};

const getRecordPreciseExamAge = (record) =>
  calculatePreciseAgeAtDate(
    record?.birthday || record?.birthdate || record?.birth_date,
    record?.rawDate || record?.dateadd || record?.date,
  );

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4;
      margin: 8mm 10mm;
    }

    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body {
      margin: 0;
      padding: 0;
    }

    body * {
      visibility: hidden;
    }

    #health-report-modal,
    #health-report-modal * {
      visibility: visible;
    }

    #health-report-modal {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      font-size: 21pt !important;
    }

    /* ขยาย font-size สำหรับ print (x1.5) */
    #health-report-modal h1 {
      font-size: 22pt !important;
    }

    #health-report-modal .print-title {
      font-size: 22pt !important;
    }

    #health-report-modal .print-subtitle {
      font-size: 14pt !important;
    }

    #health-report-modal .print-label,
    #health-report-modal .print-value {
      font-size: 20pt !important;
    }

    #health-report-modal .print-header {
      font-size: 23pt !important;
    }

    #health-report-modal .print-table-text {
      font-size: 18pt !important;
    }

    #health-report-modal .print-note {
      font-size: 20pt !important;
    }

    #health-report-modal .print-box-title {
      font-size: 21pt !important;
    }

    #health-report-modal .print-box-content {
      font-size: 18pt !important;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) =>
    props.$isMobileView ? "white" : "rgba(0, 0, 0, 0.5)"};
  display: flex;
  align-items: ${(props) => (props.$isMobileView ? "flex-start" : "center")};
  justify-content: center;
  z-index: 1000;
  padding: ${(props) => (props.$isMobileView ? "0" : "1rem")};
  overflow-y: ${(props) => (props.$isMobileView ? "auto" : "visible")};

  @media print {
    background-color: white;
    position: static;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 100%;
  max-width: min(210mm, 90vw);
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);

  /* Mobile view: full width, no max-width constraint */
  ${(props) =>
    props.$isMobileView &&
    `
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `}

  @media print {
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: none;
    box-shadow: none;
    overflow: visible;
    border-radius: 0;
    margin: 0;
    padding: 0;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 6vw;
  background: white;
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;

  /* Hide on mobile view */
  ${(props) =>
    props.$isMobileView &&
    `
    display: none;
  `}

  &:hover {
    background-color: #f3f4f6;
  }

  @media print {
    display: none;
  }
`;

const PrintButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 12vw;
  background: ${(props) => (props.disabled ? "#9CA3AF" : "#00B5B8")};
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  font-size: 14px;
  font-weight: 500;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  /* Hide Print button on mobile - ใช้ PDFButton แทน */
  ${(props) =>
    props.$isMobileView &&
    `
    display: none;
  `}

  &:hover {
    background-color: ${(props) => (props.disabled ? "#9CA3AF" : "#0891B2")};
  }

  @media print {
    display: none;
  }
`;

const PDFButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 19vw;
  background: ${(props) => (props.disabled ? "#9CA3AF" : "#E53E3E")};
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  font-size: 14px;
  font-weight: 500;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  ${(props) =>
    props.$isMobileView &&
    `
    right: 5vw;
    top: 3vh;
    padding: 0.5rem 1rem;
    font-size: 12px;
  `}

  &:hover {
    background-color: ${(props) => (props.disabled ? "#9CA3AF" : "#C53030")};
  }

  @media print {
    display: none;
  }
`;

const ReportContainer = styled.div`
  padding: 1.25rem 1.5rem;
  font-family: "Sarabun", sans-serif;
  overflow: visible;

  /* Mobile view: scale down to fit screen (0.5 = 50%) */
  ${(props) =>
    props.$isMobileView &&
    `
    padding: 0.5rem;
    zoom: 0.5;
    -moz-transform: scale(0.5);
    -moz-transform-origin: 0 0;
  `}

  @media print {
    padding: 0.75rem 1rem;
    width: 100%;
    box-sizing: border-box;
    transform: none;
  }
`;

const PageNumber = styled.div`
  position: relative;
  text-align: right;
  font-size: 16px;
  font-weight: 400;
  color: #111827;
  margin-bottom: 0.5rem;

  @media print {
    font-size: 18pt !important;
  }
`;

const PageBreak = styled.div`
  @media screen {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px dashed #e5e7eb;
  }

  @media print {
    page-break-after: always;
    page-break-inside: avoid;
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LogoImage = styled.img`
  height: 65px;
  width: auto;
`;

const TitleSection = styled.div`
  text-align: right;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  margin-bottom: 0.25rem;

  @media print {
    font-size: 7pt !important;
    line-height: 1.1;
    white-space: nowrap;
  }
`;

const Subtitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #374151;

  @media print {
    font-size: 15pt !important;
    white-space: nowrap;
  }
`;

const PatientInfoSection = styled.div`
  margin-bottom: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

const InfoRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
`;

const InfoLabel = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #111827;

  @media print {
    font-size: 20pt !important;
  }
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #111827;

  @media print {
    font-size: 20pt !important;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 0;
`;

const ContentSection = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
  overflow: visible;
`;

const LeftSection = styled.div`
  flex: 0 0 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HumanImage = styled.img`
  width: 220px;
  height: auto;
  margin-bottom: 1rem;
`;

const RightSection = styled.div`
  flex: 1;
  overflow: visible;
`;

const ResultHeader = styled.div`
  background: linear-gradient(135deg, #67c3f3 0%, #5ab5e5 100%);
  color: #ffffff;
  padding: 0.6rem 1.5rem;
  font-size: 18px;
  font-weight: 600;
  display: inline-block;
  border-radius: 0.5rem 0.5rem 0 0;
  margin-bottom: 0;

  @media print {
    font-size: 23pt !important;
    padding: 0.5rem 1.25rem;
  }
`;

const ResultTable = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0 0.5rem 0.5rem 0.5rem;
  overflow: hidden;
`;

const ResultRow = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
`;

const ResultRowFull = styled.div`
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ResultFullTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;

  @media print {
    font-size: 20pt !important;
  }
`;

const ResultFullContent = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #111827;
  padding-left: 1rem;

  @media print {
    font-size: 18pt !important;
  }
`;

const ResultLabel = styled.div`
  flex: 1;
  padding: 0.6rem 1rem;
  font-size: 16px;
  font-weight: ${(props) => (props.bold ? "600" : "400")};
  color: #111827;
  background-color: white;
  text-align: left;

  @media print {
    font-size: 18pt !important;
    padding: 0.4rem 0.75rem;
  }
`;

const ResultValue = styled.div`
  flex: 1;
  padding: 0.6rem 1rem;
  font-size: 16px;
  font-weight: 400;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: white;

  @media print {
    font-size: 18pt !important;
    padding: 0.4rem 0.75rem;
  }
`;

const NoteSection = styled.div`
  margin-top: 1rem;
`;

const NoteTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;

  @media print {
    font-size: 20pt !important;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  font-size: 16px;
  font-weight: 400;
  color: #111827;

  @media print {
    font-size: 18pt !important;
  }
`;

const AdditionalSection = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoBox = styled.div`
  border: 2px solid #67c3f3;
  border-radius: 0.75rem;
  padding: 1.25rem;
  page-break-inside: avoid;
`;

const InfoBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const InfoIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const InfoBoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;

  @media print {
    font-size: 21pt !important;
  }
`;

const InfoBoxContent = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: #374151;
  line-height: 1.6;

  @media print {
    font-size: 18pt !important;
    line-height: 1.5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const RiskButton = styled.button`
  background-color: transparent;
  border: 1px solid #67c3f3;
  border-radius: 0.375rem;
  color: #111827;
  padding: 0.4rem 1rem;
  font-size: 15px;
  font-weight: 400;
  cursor: default;

  @media print {
    font-size: 17pt !important;
    padding: 0.3rem 0.75rem;
  }
`;

const BloodTestTableWrapper = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  page-break-inside: avoid;
`;

const BloodTestTable = styled.div`
  border-radius: 0.5rem;
  overflow: hidden;
  width: 90%;
`;

const BloodTestHeader = styled.div`
  background: linear-gradient(135deg, #67c3f3 0%, #5ab5e5 100%);
  color: #ffffff;
  padding: 0.6rem 1.5rem;
  font-size: 17px;
  font-weight: 600;
  display: inline-block;
  border-radius: 0.5rem 0.5rem 0 0;
  margin-bottom: 0;
  align-self: flex-start;
  margin-left: 5%;

  @media print {
    font-size: 21pt !important;
    padding: 0.5rem 1.25rem;
  }
`;

const BloodTestContent = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0 0.5rem 0.5rem 0.5rem;
  overflow: hidden;
  width: 90%;
`;

const BloodTestRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 0.8fr;
  border-bottom: 1px solid #e5e7eb;
`;

const BloodTestCell = styled.div`
  padding: 0.6rem 0.85rem;
  font-size: 15px;
  font-weight: ${(props) =>
    props.header ? "600" : props.abnormal ? "600" : "400"};
  color: ${(props) =>
    props.header ? "#111827" : props.abnormal ? "#DC2626" : "#111827"};
  background-color: white;
  text-align: ${(props) => (props.center ? "center" : "left")};

  ${(props) =>
    props.abnormal &&
    `
    &::before {
      content: '⚠ ';
      font-size: 16px;
    }
  `}

  @media print {
    font-size: 17pt !important;
    padding: 0.4rem 0.6rem;
  }
`;

const AlertValue = styled.span`
  color: #dc2626;
  font-weight: 600;
  font-size: 15px;

  &::before {
    content: "⚠ ";
  }

  @media print {
    font-size: 17pt !important;
  }
`;

const UrineTestNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  font-size: 15px;
  color: #dc2626;
  width: 100%;
  padding-left: 5%;

  &::before {
    content: "⚠";
    font-size: 16px;
  }

  @media print {
    font-size: 17pt !important;
  }
`;

const HealthReportModal = ({
  isOpen,
  onClose,
  recordData,
  disableAutoPrint = false,
  isMobileView = false,
  isPublicView = false,
}) => {
  // ตรวจสอบว่า recordData เป็น array หรือ single object
  const isBatchPrint = Array.isArray(recordData);
  const recordsArray = isBatchPrint
    ? recordData
    : recordData
      ? [recordData]
      : [];

  // ฟังก์ชันดึงข้อมูล master data จาก medium.json
  const [masterData, setMasterData] = useState(null);

  const loadMasterData = async () => {
    try {
      const result = await referenceDataAPI.getAllParameters();
      if (result.status === "success" && result.data) {
        setMasterData(result.data);
      }
    } catch (error) {
      console.error("Error loading master data:", error);
    }
  };

  const normalizeGenderForMaster = (gender) => {
    const value = String(gender || "")
      .trim()
      .toLowerCase();

    if (["m", "male", "ชาย", "1", "man", "boy"].includes(value)) {
      return "M";
    }

    if (["f", "female", "หญิง", "2", "woman", "girl"].includes(value)) {
      return "F";
    }

    return "";
  };

  const normalizeAgeForMaster = (age) => {
    const numericAge = Number.parseFloat(String(age ?? ""));
    return Number.isFinite(numericAge) ? numericAge : 0;
  };

  // ฟังก์ชันหาค่าปกติจาก Master Data (medium.json)
  const getNormalRangeFromMaster = (paramName, sex, age) => {
    if (!masterData) {
      return "-";
    }

    // แปลงชื่อ parameter ให้ตรงกับ key ใน medium.json
    let searchName = paramName;

    // Blood Chemistry & Liver Function
    if (paramName === "SGOT (AST)") searchName = "SGOT (AST)";
    else if (paramName === "SGPT (ALT)") searchName = "SGPT (ALT)";
    else if (paramName === "ALP (Alkaline Phosphatase)") searchName = "ALP";
    else if (paramName === "Uric Acid") searchName = "Uric Acid";
    else if (paramName === "BUN") searchName = "BUN, Urea";
    else if (paramName === "GGT") searchName = "GGT";
    else if (paramName === "Cholesterol") searchName = "คอเลสเตอรอลรวม";
    else if (paramName === "Triglyceride") searchName = "ไตรกลีเซอไรด์";
    else if (paramName === "CHO") searchName = "คอเลสเตอรอลรวม";
    else if (paramName === "TRI") searchName = "ไตรกลีเซอไรด์";
    else if (paramName === "FBS") searchName = "Fasting blood glucose";
    else if (paramName === "Glucose") searchName = "Fasting blood glucose";
    // Urine Tests - แปลงชื่อให้ตรงกับ medium.json
    else if (paramName === "Urine Color") searchName = "Color";
    else if (paramName === "Urine Appearance") searchName = "Urine Appearance";
    else if (paramName === "Clarity") searchName = "Clarity";
    else if (paramName === "Urine pH") searchName = "pH";
    else if (paramName === "Specific Gravity") searchName = "Specific Gravity";
    else if (paramName === "Urine Protein") searchName = "Protein";
    else if (paramName === "Urine Glucose") searchName = "Sugar";
    else if (paramName === "Urine Ketone") searchName = "Ketones";
    else if (paramName === "Urine Blood") searchName = "Blood";
    else if (paramName === "Urine Leukocytes") searchName = "Leukocytes";
    else if (paramName === "Urine Nitrite") searchName = "Nitrite";
    else if (paramName === "Urine Bilirubin") searchName = "Bilirubin";
    else if (paramName === "Urobilinogen") searchName = "Urobilinogen";
    else if (paramName === "Urine RBC") searchName = "RBC (Urine)";
    else if (paramName === "Urine WBC") searchName = "WBC (Urine)";
    else if (paramName === "Urine Bacteria") searchName = "Bacteria";
    else if (paramName === "Bacteria")
      searchName = "Bacteria"; // ← เพิ่มกรณีไม่มี "Urine" ข้างหน้า
    else if (paramName === "Epithelial Cells") searchName = "เซลล์เยื่อบุผิว";
    else if (paramName === "Platelets") searchName = "Platelet";
    else if (
      paramName === "Calcium Oxalate" ||
      paramName === "Calcium oxalate crystal"
    )
      searchName = "_hide_Calcium oxalate crystal";
    else if (
      paramName === "Calcium Carbonate crystal" ||
      paramName === "Calcium Carbonate" ||
      paramName === "Calcium carbonate"
    )
      searchName = "_hide_Calcium Carbonate crystal";
    // Stool Tests
    else if (paramName === "Stool Color") searchName = "Color (Stool)";
    else if (paramName === "Stool WBC") searchName = "WBC (Stool)";
    else if (paramName === "Stool RBC") searchName = "RBC (Stool)";
    else if (paramName === "Parasite, Ova") searchName = "Parasite";
    // Hepatitis - แปลงชื่อให้ตรงกับ medium.json
    else if (paramName === "HCV Ab" || paramName === "anti-HCV")
      searchName = "HCV Ab";
    else if (paramName === "HCV Ab COI") searchName = "COI";
    // Blood Cell Count - ชื่อตรงกันอยู่แล้ว
    // WBC, RBC, Hemoglobin, Hematocrit, Platelets, MCV, MCH, MCHC
    // Neutrophile, Lymphocyte, Monocyte, Basophil, Eosinophil
    // CBC Differential % - map ไปหา key ใน medium.json (ไม่มี % suffix)
    else if (paramName === "Neutrophile %") searchName = "Neutrophile";
    else if (paramName === "Lymphocyte %") searchName = "Lymphocyte";
    else if (paramName === "Monocyte %") searchName = "Monocyte";
    else if (paramName === "Basophil %") searchName = "Basophil";
    else if (paramName === "Eosinophil %") searchName = "Eosinophil";

    const searchNames = [searchName, `_hide_${searchName}`];
    const normalizedSex = normalizeGenderForMaster(sex);
    const normalizedAge = normalizeAgeForMaster(age);

    for (const category in masterData) {
      const categoryData = masterData[category];

      for (const candidateName of searchNames) {
        const matchingKey = Object.keys(categoryData).find(
          (key) =>
            normalizeLookupText(key) === normalizeLookupText(candidateName),
        );

        if (!matchingKey || !categoryData[matchingKey]) {
          continue;
        }

        const ranges = categoryData[matchingKey];
        for (const range of ranges) {
          const rangeSex = String(range.sex || "F,M").trim();
          const sexMatch =
            rangeSex === "F,M" ||
            rangeSex === "M,F" ||
            rangeSex === normalizedSex;
          const ageMatch =
            normalizedAge >= Number(range.age_min) &&
            normalizedAge <= Number(range.age_max);

          if (sexMatch && ageMatch) {
            return range.normal_range;
          }
        }
      }
    }
    return "-";
  };

  // โหลด master data จาก medium.json เมื่อ modal เปิด
  useEffect(() => {
    if (isOpen) {
      loadMasterData();
    }
  }, [isOpen]);

  // Auto-trigger print dialog สำหรับ batch print
  useEffect(() => {
    if (
      isOpen &&
      isBatchPrint &&
      recordsArray.length > 0 &&
      !disableAutoPrint
    ) {
      // รอให้ content โหลดเสร็จก่อน
      const timer = setTimeout(() => {
        window.print();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isBatchPrint, recordsArray.length, disableAutoPrint]);

  // Early return หลัง hooks - ตรวจสอบว่า modal เปิดและมีข้อมูลหรือไม่
  if (!isOpen) return null;

  // ถ้าไม่มีข้อมูลเลย ให้ return null
  if (recordsArray.length === 0) return null;

  // ตรวจสอบว่าสถานะเป็น "approved" หรือไม่ (ใช้ record แรก)
  const isApproved = ["approved", "revised"].includes(
    String(recordsArray[0]?.approvalStatus || "").toLowerCase(),
  );

  const normalizeLookupText = (text) =>
    String(text || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  // ดึงชื่อแพทย์ผู้ตรวจ
  // - ถ้า "ส่งผลสุขภาพแล้ว" ให้ล็อกชื่อจาก approvedBy
  // - ถ้ายังไม่ส่งผล ให้แสดงชื่อคนที่ login อยู่ตามปกติ
  // - Public view ยังใช้ approvedBy เช่นเดิม
  const getDoctorName = (data) => {
    if (isPublicView) {
      return data?.approvedBy || "-";
    }

    const approvedBy =
      typeof data?.approvedBy === "string"
        ? data.approvedBy.trim()
        : data?.approvedBy;

    // ถ้ามีชื่อคนที่ส่งผล/อนุมัติแล้ว ให้ใช้ชื่อนั้นเสมอ
    // ถ้ายังไม่มีชื่อผู้ส่งจริง ค่อย fallback ไปชื่อคนที่ login อยู่
    if (approvedBy) {
      return approvedBy;
    }

    try {
      const storedUser = localStorage.getItem("mfu_user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        const prefix = user.nameprefix || "";
        const name = user.name || "";
        const lastname = user.lastname || "";
        const fullName = `${prefix} ${name} ${lastname}`.trim();
        if (fullName) return fullName;
      }
    } catch (error) {
      console.error("Error getting doctor name:", error);
    }

    return "-";
  };

  // ฟังก์ชันตรวจสอบว่าค่าผิดปกติหรือไม่
  const isAbnormal = (value, normalRange, unit = "", currentRecord = null) => {
    if (!value || value === "-" || !normalRange || normalRange === "-")
      return false;

    const parseComparableRange = (input) => {
      if (input === null || input === undefined) {
        return null;
      }

      const text = String(input).trim().replace(/,/g, "").replace(/\s+/g, "");
      const cleanedText = text.replace(
        /(cells\/hpf|cells\/lpf|\/hpf|\/lpf|cells\/ul|cells\/µl|cells\/μl)$/i,
        "",
      );

      const rangeMatch = cleanedText.match(
        /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)/,
      );
      if (rangeMatch) {
        return {
          min: parseFloat(rangeMatch[1]),
          max: parseFloat(rangeMatch[2]),
          minInclusive: true,
          maxInclusive: true,
        };
      }

      const lessThanMatch = cleanedText.match(/^<(-?\d+(?:\.\d+)?)/);
      if (lessThanMatch) {
        return {
          min: Number.NEGATIVE_INFINITY,
          max: parseFloat(lessThanMatch[1]),
          minInclusive: false,
          maxInclusive: false,
        };
      }

      const greaterThanMatch = cleanedText.match(/^>(-?\d+(?:\.\d+)?)/);
      if (greaterThanMatch) {
        return {
          min: parseFloat(greaterThanMatch[1]),
          max: Number.POSITIVE_INFINITY,
          minInclusive: false,
          maxInclusive: false,
        };
      }

      const singleValueMatch = cleanedText.match(/^(-?\d+(?:\.\d+)?)/);
      if (singleValueMatch) {
        const num = parseFloat(singleValueMatch[1]);
        return {
          min: num,
          max: num,
          minInclusive: true,
          maxInclusive: true,
        };
      }

      return null;
    };

    const isRangeWithinRange = (resultRange, normalRange) => {
      const minWithin =
        resultRange.min > normalRange.min ||
        (resultRange.min === normalRange.min &&
          (normalRange.minInclusive || !resultRange.minInclusive));
      const maxWithin =
        resultRange.max < normalRange.max ||
        (resultRange.max === normalRange.max &&
          (normalRange.maxInclusive || !resultRange.maxInclusive));

      return minWithin && maxWithin;
    };

    // text-based comparison (เช่น normalRange = "None", value = "Few")
    const normalRangeText = String(normalRange).trim();
    const valueText = String(value).trim();
    const normalRangeIsGenderFormat = /^[MF]\s/.test(normalRangeText);
    const looksNumericRange = /^[-<>]?\d/.test(
      normalRangeText.replace(/,/g, "").replace(/\s+/g, ""),
    );
    if (
      /[a-zA-Z]/.test(normalRangeText) &&
      !normalRangeIsGenderFormat &&
      !looksNumericRange
    ) {
      const normalizedResult = valueText.toLowerCase().replace(/\s+/g, "");
      const normalValues = normalRangeText
        .split(",")
        .map((v) => v.trim().toLowerCase().replace(/\s+/g, ""));
      return !normalValues.includes(normalizedResult);
    }

    const normalizedNormalRange = parseComparableRange(normalRangeText);
    const normalizedResultRange = parseComparableRange(valueText);
    if (normalizedNormalRange && normalizedResultRange) {
      return !isRangeWithinRange(normalizedResultRange, normalizedNormalRange);
    }

    // ลบ comma ออกจากตัวเลข (เช่น "6,670" -> "6670")
    const cleanValue = String(valueText).replace(/,/g, "");
    let numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return false;

    // ✅ แปลงค่าตามหน่วย: ถ้าหน่วยเป็น "×10³" หรือ "1000 cells/µL" หรือ "K/µL" ให้หารด้วย 1000
    if (
      unit &&
      (unit.includes("×10³") ||
        unit.includes("x10³") ||
        unit.includes("10^3") ||
        unit.includes("1000") ||
        unit.toLowerCase().includes("k/"))
    ) {
      numValue = numValue / 1000;
    }

    // แยกประเภทของ normal range
    // รูปแบบ: "min-max", "<max", ">min", "M x-y, F x-y", "ค่าคงที่"

    // รูปแบบ "M 12.0-18.0, F 12.0-16.0" (ขึ้นกับเพศ)
    if (normalRange.includes("M ") || normalRange.includes("F ")) {
      const gender = currentRecord?.gender || "";
      if (gender === "ชาย") {
        const maleMatch = normalRange.match(/M\s+([\d.]+)-([\d.]+)/);
        if (maleMatch) {
          const min = parseFloat(maleMatch[1]);
          const max = parseFloat(maleMatch[2]);
          return numValue < min || numValue > max;
        }
      } else if (gender === "หญิง") {
        const femaleMatch = normalRange.match(/F\s+([\d.]+)-([\d.]+)/);
        if (femaleMatch) {
          const min = parseFloat(femaleMatch[1]);
          const max = parseFloat(femaleMatch[2]);
          return numValue < min || numValue > max;
        }
      }
    } else if (
      normalRange.includes("-") &&
      !normalRange.startsWith("<") &&
      !normalRange.startsWith(">")
    ) {
      // รูปแบบ min-max เช่น "4-10" หรือ "5000-10000"
      const parts = normalRange.split("-");
      const min = parseFloat(parts[0].replace(/,/g, "").trim());
      const max = parseFloat(parts[1].replace(/,/g, "").trim());
      if (!isNaN(min) && !isNaN(max)) {
        return numValue < min || numValue > max;
      }
    } else if (normalRange.startsWith("<")) {
      // รูปแบบ <max เช่น "<200"
      const max = parseFloat(normalRange.substring(1).replace(/,/g, "").trim());
      if (!isNaN(max)) {
        return numValue >= max;
      }
    } else if (normalRange.startsWith(">")) {
      // รูปแบบ >min เช่น ">50"
      const min = parseFloat(normalRange.substring(1).replace(/,/g, "").trim());
      if (!isNaN(min)) {
        return numValue <= min;
      }
    } else if (
      normalRange === "ไม่มี" ||
      normalRange === "Not found" ||
      normalRange === "Negative"
    ) {
      // สำหรับค่าที่ต้องเป็น "ไม่มี" แต่มีค่า
      return (
        value !== "ไม่มี" &&
        value !== "Not found" &&
        value !== "Negative" &&
        value !== "-"
      );
    }

    return false;
  };

  // คำนวณ BMI Status
  const getBmiStatus = (bmi) => {
    const bmiValue = parseFloat(bmi);
    if (isNaN(bmiValue)) return "-";
    if (bmiValue < 18.5) return "น้ำหนักต่ำกว่าเกณฑ์";
    if (bmiValue < 23) return "สมส่วน";
    if (bmiValue < 25) return "น้ำหนักเกิน";
    if (bmiValue < 30) return "อ้วนเล็กน้อย";
    return "อ้วนมาก";
  };

  // ตาราง alias: แปลงชื่อที่ modal ใช้ → ชื่อที่อาจเก็บใน record (medium.json keys)
  const paramNameAliases = {
    // CBC - backward compat: old records may have saved differential as "Xxx %" names
    Neutrophile: "Neutrophile %",
    Lymphocyte: "Lymphocyte %",
    Monocyte: "Monocyte %",
    Eosinophil: "Eosinophil %",
    Basophil: "Basophil %",
    Platelets: "Platelet",
    "Platelets count": ["Platelets count", "Platelets"],
    NRBC: ["NRBC", "_hide_NRBC", "cbcnrbcresult", "nrbc"],
    Myeloblast: [
      "Myeloblast",
      "_hide_Myeloblast",
      "cbcmyeloblastresult",
      "cbc_myeloblast_result",
    ],
    Promyelocyte: [
      "Promyelocyte",
      "_hide_Promyelocyte",
      "cbcpromyelocyteresult",
      "cbc_promyelocyte_result",
    ],
    Myelocyte: [
      "Myelocyte",
      "_hide_Myelocyte",
      "cbcmyelocyteresult",
      "cbc_myelocyte_result",
    ],
    Metamyelocyte: [
      "Metamyelocyte",
      "_hide_Metamyelocyte",
      "cbcmetamyelocyteresult",
      "cbc_metamyelocyte_result",
    ],
    Lymphoblast: [
      "Lymphoblast",
      "_hide_Lymphoblast",
      "cbclymphoblastresult",
      "cbc_lymphoblast_result",
    ],
    "Band form": [
      "Band form",
      "_hide_Band form",
      "cbcbandformresult",
      "cbc_band_form_result",
    ],
    Anisocytosis: [
      "Anisocytosis",
      "_hide_Anisocytosis",
      "cbcanisocytosisresult",
      "cbc_anisocytosis_result",
    ],
    Macrocyte: [
      "Macrocyte",
      "_hide_Macrocyte",
      "cbcmacrocyteresult",
      "cbc_macrocyte_result",
    ],
    Poikilocytosis: [
      "Poikilocytosis",
      "_hide_Poikilocytosis",
      "cbcpoikilocytosisresult",
      "cbc_poikilocytosis_result",
    ],
    Ovalocyte: [
      "Ovalocyte",
      "_hide_Ovalocyte",
      "cbcovalocyteresult",
      "cbc_ovalocyte_result",
    ],
    "Target cell": [
      "Target cell",
      "_hide_Target Cell",
      "cbctargetcellresult",
      "cbc_target_cell_result",
    ],
    Schistocyte: [
      "Schistocyte",
      "_hide_Schistocyte",
      "cbcschistocyteresult",
      "cbc_schistocyte_result",
    ],
    Spherocyte: [
      "Spherocyte",
      "_hide_Spherocyte",
      "cbcspherocyteresult",
      "cbc_spherocyte_result",
    ],
    "Tear drop cell": [
      "Tear drop cell",
      "_hide_Tear drop cell",
      "cbcteardropresult",
      "cbc_tear_drop_result",
    ],
    "Burr cell": [
      "Burr cell",
      "_hide_Burr cell",
      "cbcburrcellresult",
      "cbc_burr_cell_result",
    ],
    Polychromasia: [
      "Polychromasia",
      "_hide_Polychromasia",
      "cbcpolychromasiaresult",
      "cbc_polychromasia_result",
    ],
    "Blast cell": [
      "Blast cell",
      "_hide_Blast cell",
      "cbcblastresult",
      "cbc_blast_result",
    ],
    Hypochromic: ["Hypochromic", "cbchypochromicresult", "cbchypochromic"],
    Microcyte: ["Microcyte", "cbcmicrocyteresult", "cbcmicrocyte"],
    Others: ["Others", "cbcotherresult", "cbcother"],
    // Urine
    "Urine Color": [
      "Urine Color",
      "Color",
      "urinalysiscolorresult",
      "urinalysiscolor",
    ],
    "Urine Appearance": [
      "Urine Appearance",
      "Appearance",
      "urinalysisapperanceresult",
      "urinalysisapperance",
    ],
    "Urine pH": ["pH", "urinalysisphresult", "urinalysisph"],
    "Urine Protein": [
      "Protein",
      "urinalysisproteinresult",
      "urinalysisprotein",
    ],
    "Urine Ketone": [
      "Ketones",
      "Ketone",
      "urinalysisketoneresult",
      "urinalysisketone",
    ],
    "Urine Blood": ["Blood", "urinalysisbloodresult", "urinalysisblood"],
    "Urine Leukocytes": [
      "Leukocytes",
      "urinalysisleukocyte",
      "urinalysisleukocytes",
    ],
    "Urine Nitrite": [
      "Nitrite",
      "urinalysisnitrite",
      "urinalysisnitriteresult",
    ],
    "Urine Bilirubin": [
      "Bilirubin",
      "urinalysisbilirubin",
      "urinalysisbilirubinresult",
    ],
    "Urine WBC": ["WBC (Urine)", "urinalysiswbcresult", "urinalysiswbc"],
    "Urine RBC": ["RBC (Urine)", "urinalysisrbcresult", "urinalysisrbc"],
    "Urine Bacteria": [
      "Bacteria",
      "urinalysisbactcellresult",
      "urinalysisbactcellre",
    ],
    "Epithelial Cells": [
      "Epithelial Cells",
      "Epithelial cell",
      "urinalysisepithelial",
      "urinalysisepithelialcellresult",
    ],
    "Hemoglobin detection stool": [
      "Hemoglobin detection stool",
      "stool_occult_blood",
      "occult_blood",
      "hemoglobindetectionstoolresult",
      "stoolhemoglobindetectionstoolresult",
    ],
    "Non-squamous epithelial cell": [
      "Non-squamous epithelial cell",
      "_hide_Non-squamous epithelial cell",
      "urinalysisnonsquamousepithelialcellresult",
      "urinalysisnonsquamousepithelialcell",
    ],
    "Renal tubular epithelial cell": [
      "Renal tubular epithelial cell",
      "_hide_Renal tubular epithelial cell",
      "urinalysisrenaltubularepithelialcellresult",
      "urinalysisrenaltubularepithelialcell",
    ],
    "RBC cast": [
      "RBC cast",
      "_hide_RBC cast",
      "urinalysisrbccastresult",
      "urinalysisrbccast",
      "rbc_cast",
    ],
    // Liver / Kidney
    "ALP (Alkaline Phosphatase)": "ALP",
    BUN: "BUN, Urea",
    "Non-NPO blood glucose": [
      "Non-NPO blood glucose",
      "glucosenonnpcresult",
      "nonnpobloodglucose",
    ],
    // Lipid
    Cholesterol: [
      "Cholesterol",
      "CHO",
      "chol",
      "cholesterolresult",
      "lipidprofilecholesterolresult",
    ],
    Triglyceride: [
      "Triglyceride",
      "TRI",
      "tri",
      "tg",
      "triglycerideresult",
      "lipidprofiletriglycerideresult",
      "lipidprofiletriglyceride",
    ],
    // Casts (modal name → medium.json _hide_ name)
    "Hyaline Cast": "_hide_Hyaline cast",
    "Granular Cast": "_hide_Granular cast",
    "Cellular Cast": "_hide_Cellular cast",
    "Waxy Cast": "_hide_Waxy cast",
    // Crystals
    "Calcium Oxalate": [
      "Calcium oxalate crystal",
      "_hide_Calcium oxalate crystal",
    ],
    "Calcium Carbonate crystal": [
      "Calcium Carbonate crystal",
      "_hide_Calcium Carbonate crystal",
      "Calcium Carbonate",
      "Calcium carbonate",
    ],
    "Uric Acid Crystal": [
      "Uric Acid Crystal",
      "_hide_Uric acid crystal",
      "Uric acid crystal",
    ],
    "Triple Phosphate": "_hide_Triple phosphate crystal",
    "Calcium phosphate crystal": [
      "Calcium phosphate crystal",
      "_hide_Calcium phosphate crystal",
      "urinalysiscalciumphosphatecrystalresult",
      "urinalysiscalciumphosphatecrystal",
    ],
    "Urinalysis Others": [
      "Urinalysis Others",
      "_hide_Urinalysis Others",
      "urinalysisothersresult",
      "urinalysisothers",
      "urinalysis_others_result",
    ],
    "Mucus thread": [
      "Mucus thread",
      "_hide_Mucus thread",
      "urinalysismucusthreadresult",
      "urinalysismucusthread",
    ],
    "Cystine crystal": [
      "Cystine crystal",
      "_hide_Cystine crystal",
      "urinalysiscystinecrystalresult",
      "urinalysiscystinecrystal",
      "urinalysiscystinecryst",
      "cystine",
    ],
    CA153: [
      "CA153",
      "tumor marker CA 153 result",
      "CA 15-3",
      "ca153",
      "ca15_3",
      "ca15-3",
    ],
    "RBC morphology": [
      "RBC morphology",
      "cbcrbcmorphologyresult",
      "cbcrbcmorphology",
    ],
    Sodium: [
      "Sodium",
      "Na",
      "na",
      "electrolytesodiumresult",
      "electrolyteSodiumresult",
    ],
    Potassium: [
      "Potassium",
      "K",
      "k",
      "electrolytepotassiumresult",
      "electrolytePotassiumresult",
    ],
    Chloride: [
      "Chloride",
      "Cl",
      "cl",
      "electrolytechorideresult",
      "electrolyteChlorideresult",
    ],
    Bicarbonate: [
      "Bicarbonate",
      "HCO3",
      "hco3",
      "electrolyteco2result",
      "electrolyteCo2result",
    ],
    "Total Calcium": [
      "Total Calcium",
      "Ca",
      "ca",
      "electrolytecalciumresult",
      "electrolyteCalciumresult",
    ],
    "Inorganic phosphate": [
      "Inorganic phosphate",
      "P",
      "p",
      "electrolytephosphorusresult",
      "electrolytePhosphorusresult",
    ],
    Magnesium: [
      "Magnesium",
      "Mg",
      "mg",
      "electrolytemagnesiumresult",
      "electrolyteMagnesiumresult",
    ],
  };

  const normalizeParameterLookup = (text) =>
    String(text || "")
      .toLowerCase()
      .replace(/^_hide_/, "")
      .replace(/\s+/g, "")
      .replace(/[_-]/g, "")
      .trim();

  const getParameterLookupNames = (name) => {
    const alias = paramNameAliases[name];
    const lookupNames = [name];

    if (Array.isArray(alias)) {
      lookupNames.push(...alias);
    } else if (alias) {
      lookupNames.push(alias);
    }

    return [...new Set(lookupNames)].filter(Boolean);
  };

  const findExactParameterMatch = (parameters, name) => {
    if (!Array.isArray(parameters) || !name) {
      return null;
    }

    const normalizedCandidates = getParameterLookupNames(name)
      .map((candidate) => normalizeParameterLookup(candidate))
      .filter(Boolean);

    if (normalizedCandidates.length === 0) {
      return null;
    }

    return (
      parameters.find((param) =>
        normalizedCandidates.includes(normalizeParameterLookup(param?.name)),
      ) || null
    );
  };

  // ดึงข้อมูล parameter จาก parameters array (รองรับทั้ง exact match และ alias)
  const getParameterValue = (name, currentRecord) => {
    const getRecordParameters = (record) =>
      Array.isArray(record?.parameters) && record.parameters.length > 0
        ? record.parameters
        : getUnifiedParameters(record || recordData, masterData);

    const visibleParams = getRecordParameters(currentRecord);
    let param = findExactParameterMatch(visibleParams, name);
    if (param) return param?.result || "-";
    return "-";
  };

  const getParameterNormal = (name, currentRecord) => {
    // ✅ ดึงค่าปกติจาก medium.json เสมอ
    const sex = normalizeGenderForMaster(currentRecord?.gender);
    // ✅ ใช้อายุแบบละเอียด (ทศนิยมของปี) เพื่อจับคู่ช่วงอายุแบบวัน/เดือนของทารกแรกเกิดใน medium.json
    const age = normalizeAgeForMaster(
      getRecordPreciseExamAge(currentRecord) ?? currentRecord?.age,
    );
    const alias = paramNameAliases[name];

    // ลองชื่อที่แสดงผลก่อน แล้วค่อยไล่ alias ทีละตัว
    const lookupNames = [name];
    if (Array.isArray(alias)) {
      lookupNames.push(...alias);
    } else if (alias) {
      lookupNames.push(alias);
    }

    const uniqueLookupNames = [...new Set(lookupNames)];
    for (const lookupName of uniqueLookupNames) {
      if (!lookupName) {
        continue;
      }
      const normalRange = getNormalRangeFromMaster(lookupName, sex, age);
      if (normalRange && normalRange !== "-") {
        return normalRange;
      }
    }

    return "-";
  };

  const getParameterUnit = (name, currentRecord) => {
    const record =
      currentRecord || (Array.isArray(recordData) ? recordData[0] : recordData);

    const getRecordParameters = (targetRecord) =>
      Array.isArray((targetRecord || record)?.parameters) &&
      (targetRecord || record).parameters.length > 0
        ? (targetRecord || record).parameters
        : getUnifiedParameters(targetRecord || record, masterData);

    const visibleParams = getRecordParameters(record);
    let param = findExactParameterMatch(visibleParams, name);
    if (param) return param?.unit || "";
    return "";
  };

  const getUrineUricAcidCrystalFields = (currentRecord) => {
    const labData = currentRecord?.labData || {};
    const value =
      labData["urinalysisuricacidcrystalresult"] ||
      labData["urinalysis uric acid crystal result"] ||
      labData["urinalysisuricacidcrystal"] ||
      labData["urinalysis uric acid crystal"] ||
      labData["uric acid crystal result"] ||
      "";
    const normal =
      labData["urinalysisuricacidcrystalnormalvalue"] ||
      labData["urinalysis uric acid crystal normal value"] ||
      labData["urinalysisuricacidcrystalnormal"] ||
      labData["uric acid crystal normal value"] ||
      "";
    const unit =
      labData["urinalysisuricacidcrystalunit"] ||
      labData["urinalysis uric acid crystal unit"] ||
      labData["uric acid crystal unit"] ||
      "";

    return { value, normal, unit };
  };

  // ซ่อนเฉพาะพารามิเตอร์ที่อยู่ใน _hide_ list เมื่อผลตรวจตรงกับค่าปกติ
  const shouldHideParameter = (name, value, normal) => {
    return shouldHideParameterByMasterData(
      { name, result: value, normal },
      masterData,
    );
  };

  // ฟังก์ชันสร้าง data object สำหรับแต่ละ record
  const createDataObject = (currentRecord) => {
    const urineUricAcidCrystalFields =
      getUrineUricAcidCrystalFields(currentRecord);
    const sourceParameters =
      Array.isArray(currentRecord?.parameters) &&
      currentRecord.parameters.length > 0
        ? currentRecord.parameters
        : getUnifiedParameters(currentRecord, masterData);
    const electrolyteVisible = [
      "Sodium",
      "Potassium",
      "Chloride",
      "Bicarbonate",
      "Total Calcium",
      "Inorganic phosphate",
      "Magnesium",
    ].some((paramName) => findExactParameterMatch(sourceParameters, paramName));

    return {
      patientType: currentRecord?.company || currentRecord?.campaign || "-",
      patientName: currentRecord?.patientName || "-",
      age: (getRecordExamAge(currentRecord) ?? currentRecord?.age) || "-",
      gender: currentRecord?.gender || "-",
      hn: currentRecord?.hn || "-",
      date: (() => {
        const datePart = currentRecord?.date || "-";
        const raw = currentRecord?.rawDate || "";
        if (raw && raw.includes(" ")) {
          const timePart = raw.split(" ")[1]?.substring(0, 5);
          return timePart ? `${datePart} เวลา ${timePart} น.` : datePart;
        }
        return datePart;
      })(),
      weight: currentRecord?.weight ? `${currentRecord.weight} กิโลกรัม` : "-",
      height: currentRecord?.height ? `${currentRecord.height} เซนติเมตร` : "-",
      bmi: currentRecord?.bmi ? `${currentRecord.bmi} กก./ม.` : "-",
      bmiStatus: getBmiStatus(currentRecord?.bmi),
      bloodPressure: currentRecord?.bloodPressure
        ? `${currentRecord.bloodPressure} มม.ปรอก`
        : "-",
      pulse: currentRecord?.pulse ? `${currentRecord.pulse} ครั้ง/นาที` : "-",
      diseases: currentRecord?.diseases || "ไม่มี",
      doctorNote:
        currentRecord?.doctorNote ||
        currentRecord?.doctor_advice ||
        currentRecord?.doctorAdvice ||
        "-",
      foodAllergies: currentRecord?.foodAllergies || "-",
      tags:
        (Array.isArray(currentRecord?.templates) &&
          currentRecord.templates.length > 0 &&
          currentRecord.templates) ||
        currentRecord?.matchedTemplates
          ?.map((template) => template?.template_name)
          .filter(Boolean) ||
        currentRecord?.health_risk_tags ||
        currentRecord?.tags ||
        [],
      // คำแนะนำจาก AI
      approvedBy: currentRecord?.approvedBy || null,
      nutritionAdvice: currentRecord?.nutritionAdvice || "",
      exerciseAdvice: currentRecord?.exerciseAdvice || "",
      sleepAdvice: currentRecord?.sleepAdvice || "",
      // ข้อมูล Lab Results - หน้า 2
      wbc: getParameterValue("WBC", currentRecord),
      wbcNormal: getParameterNormal("WBC", currentRecord),
      wbcUnit: getParameterUnit("WBC"),
      hemoglobin: getParameterValue("Hemoglobin", currentRecord),
      hemoglobinNormal: getParameterNormal("Hemoglobin", currentRecord),
      hemoglobinUnit: getParameterUnit("Hemoglobin"),
      hematocrit: getParameterValue("Hematocrit", currentRecord),
      hematocritNormal: getParameterNormal("Hematocrit", currentRecord),
      hematocritUnit: getParameterUnit("Hematocrit"),
      plateletsCount: getParameterValue("Platelets count", currentRecord),
      plateletsCountNormal: getParameterNormal(
        "Platelets count",
        currentRecord,
      ),
      plateletsCountUnit: getParameterUnit("Platelets count"),
      platelet: getParameterValue("Platelets", currentRecord),
      plateletNormal: getParameterNormal("Platelets", currentRecord),
      plateletUnit: getParameterUnit("Platelets"),
      neutrophile: getParameterValue("Neutrophile", currentRecord),
      neutrophileNormal: getParameterNormal("Neutrophile", currentRecord),
      neutrophileUnit: getParameterUnit("Neutrophile"),
      lymphocyte: getParameterValue("Lymphocyte", currentRecord),
      lymphocyteNormal: getParameterNormal("Lymphocyte", currentRecord),
      lymphocyteUnit: getParameterUnit("Lymphocyte"),
      monocyte: getParameterValue("Monocyte", currentRecord),
      monocyteNormal: getParameterNormal("Monocyte", currentRecord),
      monocyteUnit: getParameterUnit("Monocyte"),
      basophil: getParameterValue("Basophil", currentRecord),
      basophilNormal: getParameterNormal("Basophil", currentRecord),
      basophilUnit: getParameterUnit("Basophil"),
      eosinophil: getParameterValue("Eosinophil", currentRecord),
      eosinophilNormal: getParameterNormal("Eosinophil", currentRecord),
      eosinophilUnit: getParameterUnit("Eosinophil"),
      // CBC Percentages - หน้า 2
      neutrophilePercent: getParameterValue("Neutrophile %", currentRecord),
      neutrophilePercentNormal: getParameterNormal(
        "Neutrophile %",
        currentRecord,
      ),
      neutrophilePercentUnit: getParameterUnit("Neutrophile %"),
      lymphocytePercent: getParameterValue("Lymphocyte %", currentRecord),
      lymphocytePercentNormal: getParameterNormal(
        "Lymphocyte %",
        currentRecord,
      ),
      lymphocytePercentUnit: getParameterUnit("Lymphocyte %"),
      monocytePercent: getParameterValue("Monocyte %", currentRecord),
      monocytePercentNormal: getParameterNormal("Monocyte %", currentRecord),
      monocytePercentUnit: getParameterUnit("Monocyte %"),
      eosinophilPercent: getParameterValue("Eosinophil %", currentRecord),
      eosinophilPercentNormal: getParameterNormal(
        "Eosinophil %",
        currentRecord,
      ),
      eosinophilPercentUnit: getParameterUnit("Eosinophil %"),
      basophilPercent: getParameterValue("Basophil %", currentRecord),
      basophilPercentNormal: getParameterNormal("Basophil %", currentRecord),
      basophilPercentUnit: getParameterUnit("Basophil %"),
      nrbc: getParameterValue("NRBC", currentRecord),
      nrbcNormal: getParameterNormal("NRBC", currentRecord),
      nrbcUnit: getParameterUnit("NRBC"),
      myeloblast: getParameterValue("Myeloblast", currentRecord),
      myeloblastNormal: getParameterNormal("Myeloblast", currentRecord),
      myeloblastUnit: getParameterUnit("Myeloblast"),
      promyelocyte: getParameterValue("Promyelocyte", currentRecord),
      promyelocyteNormal: getParameterNormal("Promyelocyte", currentRecord),
      promyelocyteUnit: getParameterUnit("Promyelocyte"),
      myelocyte: getParameterValue("Myelocyte", currentRecord),
      myelocyteNormal: getParameterNormal("Myelocyte", currentRecord),
      myelocyteUnit: getParameterUnit("Myelocyte"),
      metamyelocyte: getParameterValue("Metamyelocyte", currentRecord),
      metamyelocyteNormal: getParameterNormal("Metamyelocyte", currentRecord),
      metamyelocyteUnit: getParameterUnit("Metamyelocyte"),
      lymphoblast: getParameterValue("Lymphoblast", currentRecord),
      lymphoblastNormal: getParameterNormal("Lymphoblast", currentRecord),
      lymphoblastUnit: getParameterUnit("Lymphoblast"),
      bandForm: getParameterValue("Band form", currentRecord),
      bandFormNormal: getParameterNormal("Band form", currentRecord),
      bandFormUnit: getParameterUnit("Band form"),
      anisocytosis: getParameterValue("Anisocytosis", currentRecord),
      anisocytosisNormal: getParameterNormal("Anisocytosis", currentRecord),
      anisocytosisUnit: getParameterUnit("Anisocytosis"),
      macrocyte: getParameterValue("Macrocyte", currentRecord),
      macrocyteNormal: getParameterNormal("Macrocyte", currentRecord),
      macrocyteUnit: getParameterUnit("Macrocyte"),
      poikilocytosis: getParameterValue("Poikilocytosis", currentRecord),
      poikilocytosisNormal: getParameterNormal("Poikilocytosis", currentRecord),
      poikilocytosisUnit: getParameterUnit("Poikilocytosis"),
      ovalocyte: getParameterValue("Ovalocyte", currentRecord),
      ovalocyteNormal: getParameterNormal("Ovalocyte", currentRecord),
      ovalocyteUnit: getParameterUnit("Ovalocyte"),
      targetCell: getParameterValue("Target cell", currentRecord),
      targetCellNormal: getParameterNormal("Target cell", currentRecord),
      targetCellUnit: getParameterUnit("Target cell"),
      schistocyte: getParameterValue("Schistocyte", currentRecord),
      schistocyteNormal: getParameterNormal("Schistocyte", currentRecord),
      schistocyteUnit: getParameterUnit("Schistocyte"),
      spherocyte: getParameterValue("Spherocyte", currentRecord),
      spherocyteNormal: getParameterNormal("Spherocyte", currentRecord),
      spherocyteUnit: getParameterUnit("Spherocyte"),
      tearDropCell: getParameterValue("Tear drop cell", currentRecord),
      tearDropCellNormal: getParameterNormal("Tear drop cell", currentRecord),
      tearDropCellUnit: getParameterUnit("Tear drop cell"),
      burrCell: getParameterValue("Burr cell", currentRecord),
      burrCellNormal: getParameterNormal("Burr cell", currentRecord),
      burrCellUnit: getParameterUnit("Burr cell"),
      polychromasia: getParameterValue("Polychromasia", currentRecord),
      polychromasiaNormal: getParameterNormal("Polychromasia", currentRecord),
      polychromasiaUnit: getParameterUnit("Polychromasia"),
      blastCell: getParameterValue("Blast cell", currentRecord),
      blastCellNormal: getParameterNormal("Blast cell", currentRecord),
      blastCellUnit: getParameterUnit("Blast cell"),
      hypochromic: getParameterValue("Hypochromic", currentRecord),
      hypochromicNormal: getParameterNormal("Hypochromic", currentRecord),
      hypochromicUnit: getParameterUnit("Hypochromic"),
      microcyte: getParameterValue("Microcyte", currentRecord),
      microcyteNormal: getParameterNormal("Microcyte", currentRecord),
      microcyteUnit: getParameterUnit("Microcyte"),
      // RBC และ RBC Index - หน้า 2
      rbc: getParameterValue("RBC", currentRecord),
      rbcNormal: getParameterNormal("RBC", currentRecord),
      rbcUnit: getParameterUnit("RBC"),
      rbcMorphology: getParameterValue("RBC morphology", currentRecord),
      rbcMorphologyNormal: getParameterNormal("RBC morphology", currentRecord),
      rbcMorphologyUnit: getParameterUnit("RBC morphology"),
      mcv: getParameterValue("MCV", currentRecord),
      mcvNormal: getParameterNormal("MCV", currentRecord),
      mcvUnit: getParameterUnit("MCV"),
      mch: getParameterValue("MCH", currentRecord),
      mchNormal: getParameterNormal("MCH", currentRecord),
      mchUnit: getParameterUnit("MCH"),
      mchc: getParameterValue("MCHC", currentRecord),
      mchcNormal: getParameterNormal("MCHC", currentRecord),
      mchcUnit: getParameterUnit("MCHC"),
      // HbA1C และ Glucose - หน้า 2
      glucose: getParameterValue("Fasting blood glucose", currentRecord),
      glucoseNormal: getParameterNormal("Fasting blood glucose", currentRecord),
      glucoseUnit: getParameterUnit("Fasting blood glucose"),
      nonNpoGlucose: getParameterValue("Non-NPO blood glucose", currentRecord),
      nonNpoGlucoseNormal: getParameterNormal(
        "Non-NPO blood glucose",
        currentRecord,
      ),
      nonNpoGlucoseUnit: getParameterUnit("Non-NPO blood glucose"),
      hba1c: getParameterValue("HbA1C", currentRecord),
      hba1cNormal: getParameterNormal("HbA1C", currentRecord),
      hba1cUnit: getParameterUnit("HbA1C"),
      eag: getParameterValue("eAG", currentRecord),
      eagNormal: getParameterNormal("eAG", currentRecord),
      eagUnit: getParameterUnit("eAG"),
      // ไขมันในเลือด - หน้า 3
      totalCholesterol: getParameterValue("Cholesterol", currentRecord),
      totalCholesterolNormal: getParameterNormal("Cholesterol", currentRecord),
      totalCholesterolUnit: getParameterUnit("Cholesterol"),
      triglyceride: getParameterValue("Triglyceride", currentRecord),
      triglycerideNormal: getParameterNormal("Triglyceride", currentRecord),
      triglycerideUnit: getParameterUnit("Triglyceride"),
      hdl: getParameterValue("HDL", currentRecord),
      hdlNormal: getParameterNormal("HDL", currentRecord),
      hdlUnit: getParameterUnit("HDL"),
      ldl: getParameterValue("LDL", currentRecord),
      ldlNormal: getParameterNormal("LDL", currentRecord),
      ldlUnit: getParameterUnit("LDL"),
      // การทำงานของไต - หน้า 3
      creatinine: getParameterValue("Creatinine", currentRecord),
      creatinineNormal: getParameterNormal("Creatinine", currentRecord),
      creatinineUnit: getParameterUnit("Creatinine"),
      bun: getParameterValue("BUN", currentRecord),
      bunNormal: getParameterNormal("BUN", currentRecord),
      bunUnit: getParameterUnit("BUN"),
      egfr: getParameterValue("eGFR", currentRecord),
      egfrNormal: getParameterNormal("eGFR", currentRecord),
      egfrUnit: getParameterUnit("eGFR"),
      // การทำงานของตับ - หน้า 3
      sgpt: getParameterValue("SGPT (ALT)", currentRecord),
      sgptNormal: getParameterNormal("SGPT (ALT)", currentRecord),
      sgptUnit: getParameterUnit("SGPT (ALT)"),
      sgot: getParameterValue("SGOT (AST)", currentRecord),
      sgotNormal: getParameterNormal("SGOT (AST)", currentRecord),
      sgotUnit: getParameterUnit("SGOT (AST)"),
      alp: getParameterValue("ALP (Alkaline Phosphatase)", currentRecord),
      alpNormal: getParameterNormal(
        "ALP (Alkaline Phosphatase)",
        currentRecord,
      ),
      alpUnit: getParameterUnit("ALP (Alkaline Phosphatase)"),
      totalBilirubin: getParameterValue("Total Bilirubin", currentRecord),
      totalBilirubinNormal: getParameterNormal(
        "Total Bilirubin",
        currentRecord,
      ),
      totalBilirubinUnit: getParameterUnit("Total Bilirubin"),
      albumin: getParameterValue("Albumin", currentRecord),
      albuminNormal: getParameterNormal("Albumin", currentRecord),
      albuminUnit: getParameterUnit("Albumin"),
      totalProtein: getParameterValue("Total Protein", currentRecord),
      totalProteinNormal: getParameterNormal("Total Protein", currentRecord),
      totalProteinUnit: getParameterUnit("Total Protein"),
      ggt: getParameterValue("GGT", currentRecord),
      ggtNormal: getParameterNormal("GGT", currentRecord),
      ggtUnit: getParameterUnit("GGT"),
      globulin: getParameterValue("Globulin", currentRecord),
      globulinNormal: getParameterNormal("Globulin", currentRecord),
      globulinUnit: getParameterUnit("Globulin"),
      directBilirubin:
        getParameterValue("Direct bilirubin", currentRecord) !== "-"
          ? getParameterValue("Direct bilirubin", currentRecord)
          : getParameterValue("Direct Bilirubin", currentRecord),
      directBilirubinNormal: getParameterNormal(
        "Direct bilirubin",
        currentRecord,
      ),
      directBilirubinUnit:
        getParameterUnit("Direct bilirubin") ||
        getParameterUnit("Direct Bilirubin"),
      // กรดยูริค - หน้า 3
      uricAcid: getParameterValue("Uric Acid", currentRecord),
      uricAcidNormal: getParameterNormal("Uric Acid", currentRecord),
      uricAcidUnit: getParameterUnit("Uric Acid"),
      // ผลการตรวจปัสสาวะเพิ่มเติม - หน้า 4
      urineColor: getParameterValue("Urine Color", currentRecord),
      urineColorNormal: getParameterNormal("Urine Color", currentRecord),
      urineColorUnit: getParameterUnit("Urine Color"),
      urineClarity: getParameterValue("Clarity", currentRecord),
      urineClarityNormal: getParameterNormal("Clarity", currentRecord),
      urineClarityUnit: getParameterUnit("Clarity"),
      urineAppearance: getParameterValue("Urine Appearance", currentRecord),
      urineAppearanceNormal: getParameterNormal(
        "Urine Appearance",
        currentRecord,
      ),
      urineAppearanceUnit: getParameterUnit("Urine Appearance"),
      urinePh: getParameterValue("Urine pH", currentRecord),
      urinePhNormal: getParameterNormal("Urine pH", currentRecord),
      urinePhUnit: getParameterUnit("Urine pH"),
      urineSpecificGravity: getParameterValue(
        "Specific Gravity",
        currentRecord,
      ),
      urineSpecificGravityNormal: getParameterNormal(
        "Specific Gravity",
        currentRecord,
      ),
      urineSpecificGravityUnit: getParameterUnit("Specific Gravity"),
      urineProtein: getParameterValue("Urine Protein", currentRecord),
      urineProteinNormal: getParameterNormal("Urine Protein", currentRecord),
      urineProteinUnit: getParameterUnit("Urine Protein"),
      urineSugar: getParameterValue("Urine Glucose", currentRecord),
      urineSugarNormal: getParameterNormal("Urine Glucose", currentRecord),
      urineSugarUnit: getParameterUnit("Urine Glucose"),
      urineKetones: getParameterValue("Urine Ketone", currentRecord),
      urineKetonesNormal: getParameterNormal("Urine Ketone", currentRecord),
      urineKetonesUnit: getParameterUnit("Urine Ketone"),
      urineBlood: getParameterValue("Urine Blood", currentRecord),
      urineBloodNormal: getParameterNormal("Urine Blood", currentRecord),
      urineBloodUnit: getParameterUnit("Urine Blood"),
      urineWbc: getParameterValue("Urine Leukocytes", currentRecord),
      urineWbcNormal: getParameterNormal("Urine Leukocytes", currentRecord),
      urineWbcUnit: getParameterUnit("Urine Leukocytes"),
      urineNitrite: getParameterValue("Urine Nitrite", currentRecord),
      urineNitriteNormal: getParameterNormal("Urine Nitrite", currentRecord),
      urineNitriteUnit: getParameterUnit("Urine Nitrite"),
      urineBilirubin: getParameterValue("Urine Bilirubin", currentRecord),
      urineBilirubinNormal: getParameterNormal(
        "Urine Bilirubin",
        currentRecord,
      ),
      urineBilirubinUnit: getParameterUnit("Urine Bilirubin"),
      urineUrobilinogen: getParameterValue("Urobilinogen", currentRecord),
      urineUrobilinogenNormal: getParameterNormal(
        "Urobilinogen",
        currentRecord,
      ),
      urineUrobilinogenUnit: getParameterUnit("Urobilinogen"),
      urineRbc: getParameterValue("Urine RBC", currentRecord),
      urineRbcNormal: getParameterNormal("Urine RBC", currentRecord),
      urineRbcUnit: getParameterUnit("Urine RBC"),
      urineWbcMicroscopic: getParameterValue("Urine WBC", currentRecord),
      urineWbcMicroscopicNormal: getParameterNormal("Urine WBC", currentRecord),
      urineWbcMicroscopicUnit: getParameterUnit("Urine WBC"),
      urineBacteria: getParameterValue("Urine Bacteria", currentRecord),
      urineBacteriaNormal: getParameterNormal("Urine Bacteria", currentRecord),
      urineBacteriaUnit: getParameterUnit("Urine Bacteria"),
      urineEpithelial: getParameterValue("Epithelial Cells", currentRecord),
      urineEpithelialNormal: getParameterNormal(
        "Epithelial Cells",
        currentRecord,
      ),
      urineEpithelialUnit: getParameterUnit("Epithelial Cells"),
      urineOthers: getParameterValue("Urinalysis Others", currentRecord),
      urineOthersNormal: getParameterNormal("Urinalysis Others", currentRecord),
      urineOthersUnit: getParameterUnit("Urinalysis Others"),
      mucusThread: getParameterValue("Mucus thread", currentRecord),
      mucusThreadNormal: getParameterNormal("Mucus thread", currentRecord),
      mucusThreadUnit: getParameterUnit("Mucus thread"),
      urineNonSquamousEpithelialCell: getParameterValue(
        "Non-squamous epithelial cell",
        currentRecord,
      ),
      urineNonSquamousEpithelialCellNormal: getParameterNormal(
        "Non-squamous epithelial cell",
        currentRecord,
      ),
      urineNonSquamousEpithelialCellUnit: getParameterUnit(
        "Non-squamous epithelial cell",
      ),
      urineRenalTubularEpithelialCell: getParameterValue(
        "Renal tubular epithelial cell",
        currentRecord,
      ),
      urineRenalTubularEpithelialCellNormal: getParameterNormal(
        "Renal tubular epithelial cell",
        currentRecord,
      ),
      urineRenalTubularEpithelialCellUnit: getParameterUnit(
        "Renal tubular epithelial cell",
      ),
      // Urinalysis Casts - หน้า 4
      urineHyalineCast: getParameterValue("Hyaline Cast", currentRecord),
      urineHyalineCastNormal: getParameterNormal("Hyaline Cast", currentRecord),
      urineHyalineCastUnit: getParameterUnit("Hyaline Cast"),
      urineGranularCast: getParameterValue("Granular Cast", currentRecord),
      urineGranularCastNormal: getParameterNormal(
        "Granular Cast",
        currentRecord,
      ),
      urineGranularCastUnit: getParameterUnit("Granular Cast"),
      urineCellularCast: getParameterValue("Cellular Cast", currentRecord),
      urineCellularCastNormal: getParameterNormal(
        "Cellular Cast",
        currentRecord,
      ),
      urineCellularCastUnit: getParameterUnit("Cellular Cast"),
      urineWaxyCast: getParameterValue("Waxy Cast", currentRecord),
      urineWaxyCastNormal: getParameterNormal("Waxy Cast", currentRecord),
      urineWaxyCastUnit: getParameterUnit("Waxy Cast"),
      urineRbcCast: getParameterValue("RBC cast", currentRecord),
      urineRbcCastNormal: getParameterNormal("RBC cast", currentRecord),
      urineRbcCastUnit: getParameterUnit("RBC cast"),
      // Urinalysis Crystals - หน้า 4
      urineCalciumOxalate: getParameterValue("Calcium Oxalate", currentRecord),
      urineCalciumOxalateNormal: getParameterNormal(
        "Calcium Oxalate",
        currentRecord,
      ),
      urineCalciumOxalateUnit: getParameterUnit("Calcium Oxalate"),
      urineCalciumCarbonate: getParameterValue(
        "Calcium Carbonate crystal",
        currentRecord,
      ),
      urineCalciumCarbonateNormal: getParameterNormal(
        "Calcium Carbonate crystal",
        currentRecord,
      ),
      urineCalciumCarbonateUnit: getParameterUnit("Calcium Carbonate crystal"),
      urineUricAcidCrystal:
        urineUricAcidCrystalFields.value ||
        getParameterValue("Uric Acid Crystal", currentRecord),
      urineUricAcidCrystalNormal:
        getParameterNormal("Uric Acid Crystal", currentRecord) ||
        urineUricAcidCrystalFields.normal ||
        "-",
      urineUricAcidCrystalUnit:
        urineUricAcidCrystalFields.unit ||
        getParameterUnit("Uric Acid Crystal"),
      urineTriplePhosphate: getParameterValue(
        "Triple Phosphate",
        currentRecord,
      ),
      urineTriplePhosphateNormal: getParameterNormal(
        "Triple Phosphate",
        currentRecord,
      ),
      urineTriplePhosphateUnit: getParameterUnit("Triple Phosphate"),
      urineCalciumPhosphate: getParameterValue(
        "Calcium phosphate crystal",
        currentRecord,
      ),
      urineCalciumPhosphateNormal: getParameterNormal(
        "Calcium phosphate crystal",
        currentRecord,
      ),
      urineCalciumPhosphateUnit: getParameterUnit("Calcium phosphate crystal"),
      urineCystineCrystal: getParameterValue("Cystine crystal", currentRecord),
      urineCystineCrystalNormal: getParameterNormal(
        "Cystine crystal",
        currentRecord,
      ),
      urineCystineCrystalUnit: getParameterUnit("Cystine crystal"),
      // ผลการตรวจอุจจาระ - หน้า 4
      stoolColor:
        findExactParameterMatch(sourceParameters, "Stool Color")?.result || "-",
      stoolColorNormal: getParameterNormal("Stool Color", currentRecord),
      stoolColorUnit:
        findExactParameterMatch(sourceParameters, "Stool Color")?.unit || "",
      stoolWbc:
        findExactParameterMatch(sourceParameters, "Stool WBC")?.result || "-",
      stoolWbcNormal: getParameterNormal("Stool WBC", currentRecord),
      stoolWbcUnit:
        findExactParameterMatch(sourceParameters, "Stool WBC")?.unit || "",
      stoolRbc:
        findExactParameterMatch(sourceParameters, "Stool RBC")?.result || "-",
      stoolRbcNormal: getParameterNormal("Stool RBC", currentRecord),
      stoolRbcUnit:
        findExactParameterMatch(sourceParameters, "Stool RBC")?.unit || "",
      parasite:
        findExactParameterMatch(sourceParameters, "Parasite, Ova")?.result ||
        "-",
      parasiteNormal: getParameterNormal("Parasite, Ova", currentRecord),
      parasiteUnit:
        findExactParameterMatch(sourceParameters, "Parasite, Ova")?.unit || "",
      stoolConsistency:
        findExactParameterMatch(sourceParameters, "Consistency")?.result || "-",
      stoolConsistencyNormal: getParameterNormal("Consistency", currentRecord),
      stoolConsistencyUnit:
        findExactParameterMatch(sourceParameters, "Consistency")?.unit || "",
      // Hepatitis Markers - หน้า 4
      hbsag: getParameterValue("HBsAg", currentRecord),
      hbsagNormal: getParameterNormal("HBsAg", currentRecord),
      hbsagUnit: getParameterUnit("HBsAg"),
      antiHbs: getParameterValue("HBsAb", currentRecord),
      antiHbsNormal: getParameterNormal("HBsAb", currentRecord),
      antiHbsUnit: getParameterUnit("HBsAb"),
      hcvAb:
        getParameterValue("HCV Ab", currentRecord) !== "-"
          ? getParameterValue("HCV Ab", currentRecord)
          : getParameterValue("anti-HCV", currentRecord),
      hcvAbNormal: getParameterNormal("HCV Ab", currentRecord),
      hcvAbUnit: getParameterUnit("HCV Ab") || getParameterUnit("anti-HCV"),
      antiHbsConcentration: "-",
      antiHbsConcentrationNormal: "-",
      antiHbsConcentrationUnit: "-",
      hbsagConcentration: "-",
      hbsagConcentrationNormal: "-",
      hbsagConcentrationUnit: "-",
      hbsagMethodology: "-",
      hbsagMethodologyNormal: "-",
      hbsagMethodologyUnit: "-",
      coi: "-",
      coiNormal: "-",
      coiUnit: "-",
      hcvAbMethodology: "-",
      hcvAbMethodologyNormal: "-",
      hcvAbMethodologyUnit: "-",
      // การตรวจเลือดแฝงในอุจจาระ (Occult Blood)
      hemoglobinDetectionStool:
        findExactParameterMatch(sourceParameters, "Hemoglobin detection stool")
          ?.result || "-",
      hemoglobinDetectionStoolNormal: getParameterNormal(
        "Hemoglobin detection stool",
        currentRecord,
      ),
      hemoglobinDetectionStoolUnit:
        findExactParameterMatch(sourceParameters, "Hemoglobin detection stool")
          ?.unit || "",
      hemoglobinDetectionStoolMethodology: "-",
      hemoglobinDetectionStoolMethodologyNormal: "-",
      hemoglobinDetectionStoolMethodologyUnit: "-",
      hemoglobinDetectionStoolReagentKits: "-",
      hemoglobinDetectionStoolReagentKitsNormal: "-",
      hemoglobinDetectionStoolReagentKitsUnit: "-",
      hemoglobinDetectionStoolLot: "-",
      hemoglobinDetectionStoolLotNormal: "-",
      hemoglobinDetectionStoolLotUnit: "-",
      hemoglobinDetectionStoolExpiredDate: "-",
      hemoglobinDetectionStoolExpiredDateNormal: "-",
      hemoglobinDetectionStoolExpiredDateUnit: "-",
      hemoglobinDetectionStoolSensitivity: "-",
      hemoglobinDetectionStoolSensitivityNormal: "-",
      hemoglobinDetectionStoolSensitivityUnit: "-",
      hemoglobinDetectionStoolSpecificity: "-",
      hemoglobinDetectionStoolSpecificityNormal: "-",
      hemoglobinDetectionStoolSpecificityUnit: "-",
      // เกลือแร่ในเลือด (Electrolytes)
      sodium: electrolyteVisible
        ? getParameterValue("Sodium", currentRecord)
        : "-",
      sodiumNormal: electrolyteVisible
        ? getParameterNormal("Sodium", currentRecord)
        : "-",
      sodiumUnit: electrolyteVisible ? getParameterUnit("Sodium") : "-",
      potassium: electrolyteVisible
        ? getParameterValue("Potassium", currentRecord)
        : "-",
      potassiumNormal: electrolyteVisible
        ? getParameterNormal("Potassium", currentRecord)
        : "-",
      potassiumUnit: electrolyteVisible ? getParameterUnit("Potassium") : "-",
      chloride: electrolyteVisible
        ? getParameterValue("Chloride", currentRecord)
        : "-",
      chlorideNormal: electrolyteVisible
        ? getParameterNormal("Chloride", currentRecord)
        : "-",
      chlorideUnit: electrolyteVisible ? getParameterUnit("Chloride") : "-",
      bicarbonate: electrolyteVisible
        ? getParameterValue("Bicarbonate", currentRecord)
        : "-",
      bicarbonateNormal: electrolyteVisible
        ? getParameterNormal("Bicarbonate", currentRecord)
        : "-",
      bicarbonateUnit: electrolyteVisible
        ? getParameterUnit("Bicarbonate")
        : "-",
      totalCalcium: electrolyteVisible
        ? getParameterValue("Total Calcium", currentRecord)
        : "-",
      totalCalciumNormal: electrolyteVisible
        ? getParameterNormal("Total Calcium", currentRecord)
        : "-",
      totalCalciumUnit: electrolyteVisible
        ? getParameterUnit("Total Calcium")
        : "-",
      inorganicPhosphate: electrolyteVisible
        ? getParameterValue("Inorganic phosphate", currentRecord)
        : "-",
      inorganicPhosphateNormal: electrolyteVisible
        ? getParameterNormal("Inorganic phosphate", currentRecord)
        : "-",
      inorganicPhosphateUnit: electrolyteVisible
        ? getParameterUnit("Inorganic phosphate")
        : "-",
      magnesium: electrolyteVisible
        ? getParameterValue("Magnesium", currentRecord)
        : "-",
      magnesiumNormal: electrolyteVisible
        ? getParameterNormal("Magnesium", currentRecord)
        : "-",
      magnesiumUnit: electrolyteVisible ? getParameterUnit("Magnesium") : "-",
      // ค่าบ่งชี้มะเร็ง (Tumor Markers)
      afp: getParameterValue("AFP", currentRecord),
      afpNormal: getParameterNormal("AFP", currentRecord),
      afpUnit: getParameterUnit("AFP"),
      psa: getParameterValue("PSA", currentRecord),
      psaNormal: getParameterNormal("PSA", currentRecord),
      psaUnit: getParameterUnit("PSA"),
      cea: getParameterValue("CEA", currentRecord),
      ceaNormal: getParameterNormal("CEA", currentRecord),
      ceaUnit: getParameterUnit("CEA"),
      ca153: getParameterValue("CA153", currentRecord),
      ca153Normal: getParameterNormal("CA153", currentRecord),
      ca153Unit: getParameterUnit("CA153"),
      ca199: getParameterValue("CA19-9", currentRecord),
      ca199Normal: getParameterNormal("CA19-9", currentRecord),
      ca199Unit: getParameterUnit("CA19-9"),
      ca125: getParameterValue("CA125", currentRecord),
      ca125Normal: getParameterNormal("CA125", currentRecord),
      ca125Unit: getParameterUnit("CA125"),
      // ฮอร์โมนไทรอยด์ (Thyroid Hormones)
      freeT3: getParameterValue("Free T3", currentRecord),
      freeT3Normal: getParameterNormal("Free T3", currentRecord),
      freeT3Unit: getParameterUnit("Free T3"),
      freeT4: getParameterValue("Free T4", currentRecord),
      freeT4Normal: getParameterNormal("Free T4", currentRecord),
      freeT4Unit: getParameterUnit("Free T4"),
      tsh: getParameterValue("TSH", currentRecord),
      tshNormal: getParameterNormal("TSH", currentRecord),
      tshUnit: getParameterUnit("TSH"),
      // การสัมผัสสารพิษกลุ่มยาฆ่าแมลง (Pesticide Exposure)
      cholinesterase: getParameterValue("Cholinesterase", currentRecord),
      cholinesteraseNormal: getParameterNormal("Cholinesterase", currentRecord),
      cholinesteraseUnit: getParameterUnit("Cholinesterase"),
      // การตรวจร่างกายทั่วไป (Physical Examination)
      bmiValue: getParameterValue("BMI", currentRecord),
      bmiNormal: getParameterNormal("BMI", currentRecord),
      bmiUnit: getParameterUnit("BMI"),
      bpsValue: getParameterValue("BPS", currentRecord),
      bpsNormal: getParameterNormal("BPS", currentRecord),
      bpsUnit: getParameterUnit("BPS"),
      bpdValue: getParameterValue("BPD", currentRecord),
      bpdNormal: getParameterNormal("BPD", currentRecord),
      bpdUnit: getParameterUnit("BPD"),
      pulseValue: getParameterValue("Pulse", currentRecord),
      pulseNormal: getParameterNormal("Pulse", currentRecord),
      pulseUnit: getParameterUnit("Pulse"),
      // กรุ๊ปเลือด (Blood Group)
      bloodGroupValue: getParameterValue("กรุ๊ปเลือด", currentRecord),
      bloodGroupNormal: getParameterNormal("กรุ๊ปเลือด", currentRecord),
      bloodGroupUnit: getParameterUnit("กรุ๊ปเลือด"),
    };
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrint = () => {
    // เปิด Print Dialog สำหรับการพิมพ์เอกสาร
    // User เลือก printer และพิมพ์ได้ทันที
    window.print();
  };

  const handleSavePDF = () => {
    // เปิด Print Dialog ให้ user เลือก "Save as PDF" เอง
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      // ดึง record แรก (ถ้าเป็น batch ให้ดาวน์โหลดทีละคน)
      const record = recordsArray[0];

      if (!record) {
        alert("ไม่มีข้อมูลรายงาน");
        return;
      }

      // เตรียมข้อมูลสำหรับ PDF
      const reportData = preparePDFData(record);

      // เรียก API download PDF
      await pdfAPI.downloadHealthReport(reportData);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("เกิดข้อผิดพลาดในการดาวน์โหลด PDF: " + error.message);
    }
  };

  // เตรียมข้อมูลสำหรับส่งไป backend สร้าง PDF
  const preparePDFData = (record) => {
    // ดึงข้อมูลแบบเดียวกับ createDataObject() - ดึงจาก record โดยตรง
    return {
      // ชื่อแพทย์ผู้ตรวจ — ใช้ approvedBy ถ้ามี ไม่มีให้แสดง "-"
      doctor_name: record?.approvedBy || "-",

      // ข้อมูลผู้ตรวจ
      organization:
        record?.company || record?.campaign || "บริษัทมั่งมีศรีสุข จำกัด",
      hn: record?.resolvedHn || record?.hn || "",
      full_name: record?.patientName || "",
      age: (getRecordExamAge(record) ?? record?.age) || "",
      gender: record?.gender || "",
      exam_date: record?.date || new Date().toLocaleDateString("th-TH"),
      chronic_disease: record?.diseases || "ไม่มีโรคประจำตัว",

      // ผลการตรวจร่างกายทั่วไป (ดึงจาก record โดยตรง)
      physical_exam: {
        weight: record?.weight || "",
        height: record?.height || "",
        bmi: record?.bmi || "",
        bmi_status: getBmiStatus(record?.bmi),
        bp: record?.bloodPressure || "",
        pulse: record?.pulse || "",
        chronic_disease: record?.diseases || "ไม่มี",
        allergies: record?.foodAllergies || "-",
      },

      // Health Risk Tags
      health_risk_tags:
        (Array.isArray(record?.templates) &&
          record.templates.length > 0 &&
          record.templates) ||
        record?.matchedTemplates
          ?.map((template) => template?.template_name)
          .filter(Boolean) ||
        record?.health_risk_tags ||
        record?.tags ||
        [],

      // คำแนะนำจากแพทย์ (ดึงจาก record โดยตรง)
      doctor_recommendations: record?.doctorNote ? [record.doctorNote] : [],

      // คำแนะนำด้านโภชนาการ
      nutrition_recommendations: record?.nutritionAdvice
        ? [record.nutritionAdvice]
        : [],

      // คำแนะนำด้านการออกกำลังกาย
      exercise_recommendations: record?.exerciseAdvice
        ? [record.exerciseAdvice]
        : [],

      // คำแนะนำด้านการนอน
      sleep_recommendations: record?.sleepAdvice || "",

      // ผลตรวจทางห้องปฏิบัติการ (ใช้ getParameterValue แบบเดียวกับ UI)
      lab_results: prepareLabResults(record),
    };
  };

  const LAB_SECTION_ORDER = [
    "glucose",
    "cbc",
    "lipid",
    "kidney",
    "liver",
    "urine",
    "uric_acid",
    "stool",
    "occult_blood",
    "electrolytes",
    "tumor_markers",
    "thyroid",
    "pesticide",
    "physical_params",
    "blood_group",
  ];

  // เตรียมผลตรวจทางห้องปฏิบัติการ (ดึงจาก parameters แบบเดียวกับ UI)
  const prepareLabResults = (record) => {
    const labs = {};
    const sourceParameters =
      Array.isArray(record?.parameters) && record.parameters.length > 0
        ? record.parameters
        : getUnifiedParameters(record, masterData);

    const getSourceParameterExact = (name) => {
      return findExactParameterMatch(sourceParameters, name);
    };

    // Helper function เพื่อตรวจสอบว่าค่าผิดปกติหรือไม่
    const checkAbnormal = (value, normalRange, unit = "", paramName = "") => {
      if (!value || value === "-") return false;
      if (paramName === "Stool Color") return false;
      return isAbnormal(value, normalRange, unit, record);
    };

    // 1. ผลการตรวจเบาหวาน
    const glucoseTests = [];
    const glucose = getParameterValue("Fasting blood glucose", record);
    const glucoseNormal = getParameterNormal("Fasting blood glucose", record);
    const glucoseUnit = getParameterUnit("Fasting blood glucose");
    if (glucose && glucose !== "-") {
      glucoseTests.push({
        name: "น้ำตาลในเลือดขณะอดอาหาร",
        result: glucose,
        normal_range: glucoseNormal,
        unit: glucoseUnit,
        abnormal: checkAbnormal(glucose, glucoseNormal, glucoseUnit),
      });
    }

    const nonNpoGlucose = getParameterValue("Non-NPO blood glucose", record);
    const nonNpoGlucoseNormal = getParameterNormal(
      "Non-NPO blood glucose",
      record,
    );
    const nonNpoGlucoseUnit = getParameterUnit("Non-NPO blood glucose");
    if (nonNpoGlucose && nonNpoGlucose !== "-") {
      glucoseTests.push({
        name: "น้ำตาลในเลือดหลังอาหาร",
        result: nonNpoGlucose,
        normal_range: nonNpoGlucoseNormal,
        unit: nonNpoGlucoseUnit,
        abnormal: checkAbnormal(
          nonNpoGlucose,
          nonNpoGlucoseNormal,
          nonNpoGlucoseUnit,
        ),
      });
    }

    const hba1c = getParameterValue("HbA1C", record);
    const hba1cNormal = getParameterNormal("HbA1C", record);
    const hba1cUnit = getParameterUnit("HbA1C");
    if (hba1c && hba1c !== "-") {
      glucoseTests.push({
        name: "HbA1C",
        result: hba1c,
        normal_range: hba1cNormal,
        unit: hba1cUnit,
        abnormal: checkAbnormal(hba1c, hba1cNormal, hba1cUnit),
      });
    }

    const eag = getParameterValue("eAG", record);
    const eagNormal = getParameterNormal("eAG", record);
    const eagUnit = getParameterUnit("eAG");
    if (eag && eag !== "-") {
      glucoseTests.push({
        name: "eAG",
        result: eag,
        normal_range: eagNormal,
        unit: eagUnit,
        abnormal: checkAbnormal(eag, eagNormal, eagUnit),
      });
    }

    if (glucoseTests.length > 0) {
      labs["glucose"] = glucoseTests;
    }

    // 2. ความสมบูรณ์ของเม็ดเลือด (CBC)
    const cbcTests = [];
    const cbcParams = [
      { name: "WBC", label: "เม็ดเลือดขาว (WBC)" },
      { name: "Hemoglobin", label: "ฮีโมโกลบิน (Hemoglobin)" },
      {
        name: "Hematocrit",
        label: "ฮีมาโตคริต (Hematocrit)",
      },
      { name: "Platelets count", label: "เกล็ดเลือด (Platelets count)" },
      { name: "Platelets", label: "เกล็ดเลือด (Platelet)" },
      { name: "MCV", label: "MCV" },
      { name: "MCH", label: "MCH" },
      { name: "MCHC", label: "MCHC" },
    ];

    cbcParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      // ตรวจสอบว่าควรซ่อนพารามิเตอร์หรือไม่ (_hide_ + Negative = ซ่อน)
      if (shouldHideParameter(param.name, value, normal)) {
        return; // ข้ามไม่แสดง
      }
      if (value && value !== "-") {
        cbcTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });

    if (cbcTests.length > 0) {
      labs["cbc"] = cbcTests;
    }

    // 3. CBC Differential (%)
    const cbcDiffTests = [];
    const cbcDiffParams = [
      { name: "Neutrophile", label: "นิวโทรฟิล (Neutrophile)" },
      { name: "Lymphocyte", label: "ลิมโฟไซต์ (Lymphocyte)" },
      { name: "Monocyte", label: "โมโนไซต์ (Monocyte)" },
      {
        name: "Eosinophil",
        label: "อีโอซิโนฟิล (Eosinophil)",
      },
      { name: "Basophil", label: "เบโซฟิล (Basophil)" },
    ];

    cbcDiffParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        cbcDiffTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });

    // รวม CBC Differential เข้ากับ CBC (แสดงในตารางเดียวกัน)
    if (cbcDiffTests.length > 0) {
      // เพิ่ม differential items เข้าไปใน CBC array
      cbcTests.push(...cbcDiffTests);
    }

    // 3.1 CBC Morphology / Hidden Differential
    const cbcMorphologyTests = [];
    const cbcMorphologyParams = [
      { name: "NRBC", label: "NRBC" },
      { name: "Myeloblast", label: "Myeloblast" },
      { name: "Promyelocyte", label: "Promyelocyte" },
      { name: "Myelocyte", label: "Myelocyte" },
      { name: "Metamyelocyte", label: "Metamyelocyte" },
      { name: "Lymphoblast", label: "Lymphoblast" },
      { name: "Band form", label: "Band form" },
      { name: "Anisocytosis", label: "Anisocytosis" },
      { name: "Hypochromic", label: "Hypochromic" },
      { name: "Microcyte", label: "Microcyte" },
      { name: "Macrocyte", label: "Macrocyte" },
      { name: "Poikilocytosis", label: "Poikilocytosis" },
      { name: "Ovalocyte", label: "Ovalocyte" },
      { name: "Target cell", label: "Target cell" },
      { name: "Schistocyte", label: "Schistocyte" },
      { name: "Spherocyte", label: "Spherocyte" },
      { name: "Tear drop cell", label: "Tear drop cell" },
      { name: "Burr cell", label: "Burr cell" },
      { name: "Polychromasia", label: "Polychromasia" },
      { name: "Blast cell", label: "Blast cell" },
      { name: "Others", label: "Others" },
      { name: "RBC morphology", label: "RBC morphology" },
    ];
    cbcMorphologyParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        cbcMorphologyTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });
    if (cbcMorphologyTests.length > 0) {
      cbcTests.push(...cbcMorphologyTests);
    }

    // 4. ผลการตรวจไขมันในเลือด (Lipid Profile)
    const lipidTests = [];
    const lipidParams = [
      { name: "Cholesterol", label: "Cholesterol รวม" },
      { name: "Triglyceride", label: "Triglyceride" },
      { name: "HDL", label: "HDL-Cholesterol" },
      { name: "LDL", label: "LDL-Cholesterol" },
    ];

    lipidParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        lipidTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });

    if (lipidTests.length > 0) {
      labs["lipid"] = lipidTests;
    }

    // 5. การทำงานของไต (Kidney Function)
    const kidneyTests = [];
    const kidneyParams = [
      { name: "BUN", label: "BUN" },
      { name: "Creatinine", label: "Creatinine" },
      { name: "eGFR", label: "eGFR" },
    ];

    kidneyParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        kidneyTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });

    if (kidneyTests.length > 0) {
      labs["kidney"] = kidneyTests;
    }

    // 6. การทำงานของตับ (Liver Function)
    const liverTests = [];
    const liverParams = [
      { name: "SGOT (AST)", label: "SGOT" },
      { name: "SGPT (ALT)", label: "SGPT" },
      { name: "ALP (Alkaline Phosphatase)", label: "Alkaline Phosphatase" },
      { name: "Total Bilirubin", label: "Total Bilirubin" },
      { name: "Albumin", label: "Albumin" },
      { name: "Total Protein", label: "Total Protein" },
      { name: "GGT", label: "GGT" },
      { name: "Globulin", label: "Globulin" },
    ];

    liverParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        liverTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });

    // Direct Bilirubin — fallback เหมือน modal (ลอง lowercase ก่อน แล้วค่อย TitleCase)
    const directBilirubinValue =
      getParameterValue("Direct bilirubin", record) !== "-"
        ? getParameterValue("Direct bilirubin", record)
        : getParameterValue("Direct Bilirubin", record);
    const directBilirubinNormal = getParameterNormal(
      "Direct bilirubin",
      record,
    );
    const directBilirubinUnit =
      getParameterUnit("Direct bilirubin") ||
      getParameterUnit("Direct Bilirubin");
    if (directBilirubinValue && directBilirubinValue !== "-") {
      liverTests.push({
        name: "Direct Bilirubin",
        result: directBilirubinValue,
        normal_range: directBilirubinNormal,
        unit: directBilirubinUnit,
        abnormal: checkAbnormal(
          directBilirubinValue,
          directBilirubinNormal,
          directBilirubinUnit,
        ),
      });
    }

    if (liverTests.length > 0) {
      labs["liver"] = liverTests;
    }

    // 7. ผลการตรวจปัสสาวะ (Urinalysis)
    const urineTests = [];
    const urineParams = [
      { name: "Urine Color", label: "สีของปัสสาวะ" },
      { name: "Clarity", label: "ความใส (Clarity)" },
      { name: "Urine Appearance", label: "ลักษณะ (Appearance)" },
      { name: "Specific Gravity", label: "Specific Gravity" },
      { name: "Urine pH", label: "pH" },
      { name: "Urine Protein", label: "โปรตีน (Protein)" },
      { name: "Urine Glucose", label: "น้ำตาล (Glucose)" },
      { name: "Urine Ketone", label: "Ketone" },
      { name: "Urine Bilirubin", label: "Bilirubin" },
      { name: "Urobilinogen", label: "Urobilinogen" },
      { name: "Urine Blood", label: "เม็ดเลือด (Blood)" },
      { name: "Urine Nitrite", label: "Nitrite" },
      { name: "Urine Leukocytes", label: "Leukocyte Esterase" },
      { name: "Urine WBC", label: "WBC (Microscopic)" },
      { name: "Urine RBC", label: "RBC (Microscopic)" },
      { name: "Urine Bacteria", label: "Bacteria" },
      { name: "Epithelial Cells", label: "Epithelial Cells" },
      { name: "Urinalysis Others", label: "Urinalysis Others" },
      { name: "Mucus thread", label: "Mucus thread" },
      {
        name: "Non-squamous epithelial cell",
        label: "Non-squamous epithelial cell",
      },
      {
        name: "Renal tubular epithelial cell",
        label: "Renal tubular epithelial cell",
      },
      { name: "Calcium Oxalate", label: "Calcium Oxalate" },
      {
        name: "Calcium Carbonate crystal",
        label: "Calcium Carbonate crystal",
      },
      { name: "Uric Acid Crystal", label: "Uric Acid Crystal" },
      { name: "Triple Phosphate", label: "Triple Phosphate" },
      { name: "Calcium phosphate crystal", label: "Calcium phosphate crystal" },
      { name: "Cystine crystal", label: "Cystine crystal" },
    ];

    urineParams.forEach((param) => {
      const urineUricAcidCrystalFields =
        param.name === "Uric Acid Crystal"
          ? getUrineUricAcidCrystalFields(record)
          : null;
      const value =
        urineUricAcidCrystalFields?.value ||
        getParameterValue(param.name, record);
      const normal =
        urineUricAcidCrystalFields?.normal ||
        getParameterNormal(param.name, record);
      const unit =
        urineUricAcidCrystalFields?.unit || getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        urineTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });

    if (urineTests.length > 0) {
      const urineCastParams = [
        { name: "Hyaline Cast", label: "Hyaline Cast" },
        { name: "Granular Cast", label: "Granular Cast" },
        { name: "Cellular Cast", label: "Cellular Cast" },
        { name: "Waxy Cast", label: "Waxy Cast" },
        { name: "RBC cast", label: "RBC cast" },
      ];

      urineCastParams.forEach((param) => {
        const value = getParameterValue(param.name, record);
        const normal = getParameterNormal(param.name, record);
        const unit = getParameterUnit(param.name);
        if (shouldHideParameter(param.name, value, normal)) return;
        if (value && value !== "-") {
          urineTests.push({
            name: param.label,
            result: value,
            normal_range: normal,
            unit: unit,
            abnormal: checkAbnormal(value, normal, unit),
          });
        }
      });

      labs["urine"] = urineTests;
    }

    // 10. กรดยูริคในเลือด (Uric Acid)
    const uricAcidTests = [];
    const uricAcid = getParameterValue("Uric Acid", record);
    const uricAcidNormal = getParameterNormal("Uric Acid", record);
    const uricAcidUnit = getParameterUnit("Uric Acid");
    if (uricAcid && uricAcid !== "-") {
      uricAcidTests.push({
        name: "Uric Acid",
        result: uricAcid,
        normal_range: uricAcidNormal,
        unit: uricAcidUnit,
        abnormal: checkAbnormal(uricAcid, uricAcidNormal, uricAcidUnit),
      });
    }
    if (uricAcidTests.length > 0) {
      labs["uric_acid"] = uricAcidTests;
    }

    // 11. ผลการตรวจอุจจาระ (Stool Examination)
    const stoolTests = [];
    const stoolParams = [
      { name: "Stool Color", label: "สี (Color)" },
      { name: "Stool WBC", label: "WBC" },
      { name: "Stool RBC", label: "RBC" },
      { name: "Parasite, Ova", label: "Parasite" },
      { name: "Consistency", label: "Consistency" },
    ];
    stoolParams.forEach((param) => {
      const matchedParam = getSourceParameterExact(param.name);
      const value = matchedParam?.result ?? matchedParam?.value ?? "-";
      const normal = matchedParam?.normal ?? matchedParam?.normal_range ?? "-";
      const unit = matchedParam?.unit ?? "";
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        stoolTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit, param.name),
        });
      }
    });
    if (stoolTests.length > 0) {
      labs["stool"] = stoolTests;
    }

    // 12. การตรวจเลือดแฝงในอุจจาระ (Occult Blood Test)
    const occultBloodTests = [];
    const hemoglobinStoolParam = getSourceParameterExact(
      "Hemoglobin detection stool",
    );
    const hemoglobinStool =
      hemoglobinStoolParam?.result ?? hemoglobinStoolParam?.value ?? "-";
    const hemoglobinStoolNormal =
      hemoglobinStoolParam?.normal ?? hemoglobinStoolParam?.normal_range ?? "-";
    const hemoglobinStoolUnit = hemoglobinStoolParam?.unit ?? "";
    if (hemoglobinStool && hemoglobinStool !== "-") {
      occultBloodTests.push({
        name: "เลือดแฝงในอุจจาระ",
        result: hemoglobinStool,
        normal_range: hemoglobinStoolNormal,
        unit: hemoglobinStoolUnit,
        abnormal: checkAbnormal(
          hemoglobinStool,
          hemoglobinStoolNormal,
          hemoglobinStoolUnit,
        ),
      });
    }
    if (occultBloodTests.length > 0) {
      labs["occult_blood"] = occultBloodTests;
    }

    // 13. เกลือแร่ในเลือด (Electrolytes)
    const hasExplicitElectrolyte = [
      "Sodium",
      "Potassium",
      "Chloride",
      "Bicarbonate",
      "Total Calcium",
      "Inorganic phosphate",
      "Magnesium",
    ].some((lookup) => findExactParameterMatch(sourceParameters, lookup));

    const electrolytesTests = [];
    const electrolyteParams = [
      { name: "Sodium", label: "Sodium (Na)" },
      { name: "Potassium", label: "Potassium (K)" },
      { name: "Chloride", label: "Chloride (Cl)" },
      { name: "Bicarbonate", label: "Bicarbonate (HCO3)" },
      { name: "Total Calcium", label: "Total Calcium (Ca)" },
      { name: "Inorganic phosphate", label: "Inorganic phosphate" },
      { name: "Magnesium", label: "Magnesium (Mg)" },
    ];
    electrolyteParams.forEach((param) => {
      if (!hasExplicitElectrolyte) return;
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        electrolytesTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });
    if (electrolytesTests.length > 0) {
      labs["electrolytes"] = electrolytesTests;
    }

    // 14. ไวรัสตับอักเสบ (Hepatitis Markers)
    const hepatitisTests = [];
    const hepatitisParams = [
      { name: "HBsAg", label: "HBsAg" },
      { name: "HBsAb", label: "HBsAb" },
    ];
    hepatitisParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        hepatitisTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });
    const hcvAbValue =
      getParameterValue("HCV Ab", record) !== "-"
        ? getParameterValue("HCV Ab", record)
        : getParameterValue("anti-HCV", record);
    const hcvAbNormal = getParameterNormal("HCV Ab", record);
    const hcvAbUnit =
      getParameterUnit("HCV Ab") || getParameterUnit("anti-HCV");
    if (hcvAbValue && hcvAbValue !== "-") {
      hepatitisTests.push({
        name: "HCV Ab",
        result: hcvAbValue,
        normal_range: hcvAbNormal,
        unit: hcvAbUnit,
        abnormal: checkAbnormal(hcvAbValue, hcvAbNormal, hcvAbUnit),
      });
    }
    if (hepatitisTests.length > 0) {
      labs["hepatitis"] = hepatitisTests;
    }

    // 15. ค่าบ่งชี้มะเร็ง (Tumor Markers)
    const tumorMarkerTests = [];
    const tumorMarkerParams = [
      { name: "AFP", label: "AFP" },
      { name: "PSA", label: "PSA" },
      { name: "CEA", label: "CEA" },
      { name: "CA153", label: "CA153" },
      { name: "CA19-9", label: "CA19-9" },
      { name: "CA125", label: "CA125" },
    ];
    tumorMarkerParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        tumorMarkerTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });
    if (tumorMarkerTests.length > 0) {
      labs["tumor_markers"] = tumorMarkerTests;
    }

    // 16. ฮอร์โมนไทรอยด์ (Thyroid Hormones)
    const thyroidTests = [];
    const thyroidParams = [
      { name: "Free T3", label: "Free T3" },
      { name: "Free T4", label: "Free T4" },
      { name: "TSH", label: "TSH" },
    ];
    thyroidParams.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (shouldHideParameter(param.name, value, normal)) return;
      if (value && value !== "-") {
        thyroidTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });
    if (thyroidTests.length > 0) {
      labs["thyroid"] = thyroidTests;
    }

    // 17. การสัมผัสสารพิษกลุ่มยาฆ่าแมลง (Pesticide Exposure)
    const pesticideTests = [];
    const cholinesterase = getParameterValue("Cholinesterase", record);
    const cholinesteraseNormal = getParameterNormal("Cholinesterase", record);
    const cholinesteraseUnit = getParameterUnit("Cholinesterase");
    if (cholinesterase && cholinesterase !== "-") {
      pesticideTests.push({
        name: "Cholinesterase",
        result: cholinesterase,
        normal_range: cholinesteraseNormal,
        unit: cholinesteraseUnit,
        abnormal: checkAbnormal(
          cholinesterase,
          cholinesteraseNormal,
          cholinesteraseUnit,
        ),
      });
    }
    if (pesticideTests.length > 0) {
      labs["pesticide"] = pesticideTests;
    }

    // 18. การตรวจร่างกายทั่วไป (BMI, BPS, BPD, Pulse) จาก parameters
    const physicalParamTests = [];
    const physicalParamList = [
      { name: "BMI", label: "BMI" },
      { name: "BPS", label: "ความดันบน (BPS)" },
      { name: "BPD", label: "ความดันล่าง (BPD)" },
      { name: "Pulse", label: "ชีพจร (Pulse)" },
    ];
    physicalParamList.forEach((param) => {
      const value = getParameterValue(param.name, record);
      const normal = getParameterNormal(param.name, record);
      const unit = getParameterUnit(param.name);
      if (value && value !== "-") {
        physicalParamTests.push({
          name: param.label,
          result: value,
          normal_range: normal,
          unit: unit,
          abnormal: checkAbnormal(value, normal, unit),
        });
      }
    });
    if (physicalParamTests.length > 0) {
      labs["physical_params"] = physicalParamTests;
    }

    // 19. กรุ๊ปเลือด
    const bloodGroupTests = [];
    const bloodGroupValue = getParameterValue("กรุ๊ปเลือด", record);
    const bloodGroupNormal = getParameterNormal("กรุ๊ปเลือด", record);
    const bloodGroupUnit = getParameterUnit("กรุ๊ปเลือด");
    if (bloodGroupValue && bloodGroupValue !== "-") {
      bloodGroupTests.push({
        name: "กรุ๊ปเลือด",
        result: bloodGroupValue,
        normal_range: bloodGroupNormal,
        unit: bloodGroupUnit,
        abnormal: checkAbnormal(bloodGroupValue, bloodGroupNormal, bloodGroupUnit),
      });
    }
    if (bloodGroupTests.length > 0) {
      labs["blood_group"] = bloodGroupTests;
    }

    return labs;
  };

  // แปลง labs object ที่จัดกลุ่มเป็น flat array สำหรับ backend
  const flattenLabsForBackend = (labs) => {
    const flatParameters = [];

    // วนลูปทุกหมวดตามลำดับเดียวกับสมุดสุขภาพ
    const orderedCategories = [
      ...LAB_SECTION_ORDER.filter((category) => Array.isArray(labs[category])),
      ...Object.keys(labs).filter(
        (category) => !LAB_SECTION_ORDER.includes(category),
      ),
    ];

    orderedCategories.forEach((category) => {
      if (Array.isArray(labs[category])) {
        labs[category].forEach((test) => {
          flatParameters.push({
            name: test.name,
            result: test.result,
            normal: test.normal_range,
            unit: test.unit,
          });
        });
      }
    });

    return flatParameters;
  };

  // Reusable Page Header Component
  const renderPageHeader = (pageNumber, data) => (
    <>
      <Header>
        <LogoSection>
          <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
        </LogoSection>
        <TitleSection>
          <Title>สมุดสุขภาพ (Health check up report)</Title>
          <Subtitle>แพทย์ผู้ตรวจ {getDoctorName(data)}</Subtitle>
        </TitleSection>
      </Header>
      <PatientInfoSection>
        <InfoRow>
          <InfoLabel>
            <strong>ประเภท/หน่วยงาน:</strong> {data.patientType}
          </InfoLabel>
          <InfoLabel>
            <strong>HN:</strong> {data.hn}
          </InfoLabel>
        </InfoRow>
        <InfoRow>
          <InfoRowLeft>
            <InfoLabel>
              <strong>ชื่อ-นามสกุล:</strong> {data.patientName}
            </InfoLabel>
            <InfoLabel>
              <strong>อายุ:</strong> {data.age} ปี
            </InfoLabel>
            <InfoLabel>
              <strong>เพศ:</strong> {data.gender}
            </InfoLabel>
          </InfoRowLeft>
          <InfoLabel>
            <strong>ตรวจวันที่:</strong> {data.date}
          </InfoLabel>
        </InfoRow>
      </PatientInfoSection>
      <Divider />
    </>
  );

  return (
    <>
      <PrintStyles />
      <ModalOverlay onClick={handleOverlayClick} $isMobileView={isMobileView}>
        <PDFButton onClick={handleDownloadPDF} $isMobileView={isMobileView}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          PDF
        </PDFButton>
        <PrintButton
          onClick={isMobileView ? handleSavePDF : handlePrint}
          $isMobileView={isMobileView}
        >
          {isMobileView ? (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              บันทึก PDF
            </>
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 6 2 18 2 18 9"></polyline>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                <rect x="6" y="14" width="12" height="8"></rect>
              </svg>
              Print
            </>
          )}
        </PrintButton>
        <CloseButton onClick={onClose} $isMobileView={isMobileView}>
          <X size={20} />
        </CloseButton>

        <ModalContent id="health-report-modal" $isMobileView={isMobileView}>
          {recordsArray.map((currentRecord, recordIndex) => {
            const data = createDataObject(currentRecord);

            // ตรวจสอบสถานะ approved ของแต่ละ record (สำหรับ batch print)
            const recordIsApproved = ["approved", "revised"].includes(
              String(currentRecord?.approvalStatus || "").toLowerCase(),
            );

            // ตรวจสอบว่าหน้าที่ 2 มีข้อมูลหรือไม่
            const hasPage2Data =
              data.wbc !== "-" ||
              data.hemoglobin !== "-" ||
              data.hematocrit !== "-" ||
              data.plateletsCount !== "-" ||
              data.platelet !== "-" ||
              data.neutrophile !== "-" ||
              data.lymphocyte !== "-" ||
              data.monocyte !== "-" ||
              data.basophil !== "-" ||
              data.eosinophil !== "-" ||
              data.mcv !== "-" ||
              data.mch !== "-" ||
              data.mchc !== "-" ||
              data.eag !== "-";

            // ตรวจสอบว่าหน้าที่ 3 มีข้อมูลหรือไม่
            const hasPage3Data =
              data.totalCholesterol !== "-" ||
              data.triglyceride !== "-" ||
              data.hdl !== "-" ||
              data.ldl !== "-" ||
              data.creatinine !== "-" ||
              data.bun !== "-" ||
              data.egfr !== "-" ||
              data.sgpt !== "-" ||
              data.sgot !== "-" ||
              data.alp !== "-" ||
              data.totalBilirubin !== "-" ||
              data.albumin !== "-" ||
              data.totalProtein !== "-" ||
              data.ggt !== "-" ||
              data.uricAcid !== "-";

            // ตรวจสอบว่าหน้าที่ 4 มีข้อมูลหรือไม่
            const hasPage4Data =
              data.urineColor !== "-" ||
              data.urineClarity !== "-" ||
              data.urineAppearance !== "-" ||
              data.urinePh !== "-" ||
              data.urineSpecificGravity !== "-" ||
              data.urineProtein !== "-" ||
              data.urineSugar !== "-" ||
              data.urineKetones !== "-" ||
              data.urineBlood !== "-" ||
              data.urineWbc !== "-" ||
              data.urineNitrite !== "-" ||
              data.urineBilirubin !== "-" ||
              data.urineUrobilinogen !== "-" ||
              data.urineRbc !== "-" ||
              data.urineWbcMicroscopic !== "-" ||
              data.urineBacteria !== "-" ||
              data.urineEpithelial !== "-" ||
              data.urineOthers !== "-" ||
              data.mucusThread !== "-" ||
              data.urineNonSquamousEpithelialCell !== "-" ||
              data.urineRenalTubularEpithelialCell !== "-" ||
              data.stoolColor !== "-" ||
              data.stoolWbc !== "-" ||
              data.stoolRbc !== "-" ||
              data.parasite !== "-" ||
              data.stoolConsistency !== "-";

            // ตรวจสอบว่ามีข้อมูลหมวดเพิ่มเติมหรือไม่ (ไวรัสตับอักเสบ, เกลือแร่, ค่าบ่งชี้มะเร็ง, ฮอร์โมนไทรอยด์, ยาฆ่าแมลง, เลือดแฝง)
            const hasAdditionalLabData =
              data.hbsag !== "-" ||
              data.antiHbs !== "-" ||
              data.hcvAb !== "-" ||
              data.antiHbsConcentration !== "-" ||
              data.hbsagConcentration !== "-" ||
              data.hbsagMethodology !== "-" ||
              data.coi !== "-" ||
              data.hcvAbMethodology !== "-" ||
              data.hemoglobinDetectionStool !== "-" ||
              data.hemoglobinDetectionStoolMethodology !== "-" ||
              data.hemoglobinDetectionStoolReagentKits !== "-" ||
              data.hemoglobinDetectionStoolLot !== "-" ||
              data.hemoglobinDetectionStoolExpiredDate !== "-" ||
              data.hemoglobinDetectionStoolSensitivity !== "-" ||
              data.hemoglobinDetectionStoolSpecificity !== "-" ||
              data.sodium !== "-" ||
              data.potassium !== "-" ||
              data.chloride !== "-" ||
              data.bicarbonate !== "-" ||
              data.totalCalcium !== "-" ||
              data.inorganicPhosphate !== "-" ||
              data.magnesium !== "-" ||
              data.afp !== "-" ||
              data.psa !== "-" ||
              data.cea !== "-" ||
              data.ca199 !== "-" ||
              data.ca125 !== "-" ||
              data.freeT3 !== "-" ||
              data.freeT4 !== "-" ||
              data.tsh !== "-" ||
              data.cholinesterase !== "-" ||
              data.bmiValue !== "-" ||
              data.bpsValue !== "-" ||
              data.bpdValue !== "-" ||
              data.pulseValue !== "-" ||
              data.glucose !== "-" ||
              data.nonNpoGlucose !== "-" ||
              data.hba1c !== "-" ||
              data.nrbc !== "-" ||
              data.myeloblast !== "-" ||
              data.promyelocyte !== "-" ||
              data.myelocyte !== "-" ||
              data.metamyelocyte !== "-" ||
              data.lymphoblast !== "-" ||
              data.bandForm !== "-" ||
              data.anisocytosis !== "-" ||
              data.macrocyte !== "-" ||
              data.poikilocytosis !== "-" ||
              data.ovalocyte !== "-" ||
              data.targetCell !== "-" ||
              data.schistocyte !== "-" ||
              data.spherocyte !== "-" ||
              data.tearDropCell !== "-" ||
              data.burrCell !== "-" ||
              data.polychromasia !== "-" ||
              data.blastCell !== "-" ||
              data.rbcMorphology !== "-" ||
              data.urineNonSquamousEpithelialCell !== "-" ||
              data.urineRenalTubularEpithelialCell !== "-" ||
              data.urineRbcCast !== "-" ||
              data.urineCalciumPhosphate !== "-" ||
              data.urineCystineCrystal !== "-" ||
              data.ca153 !== "-";

            return (
              <React.Fragment key={recordIndex}>
                <ReportContainer $isMobileView={isMobileView}>
                  {/* หน้าที่ 1 */}
                  <Header>
                    <LogoSection>
                      <LogoImage
                        src="/logo_mfuWC.png"
                        alt="MFU Wellness Center Logo"
                      />
                    </LogoSection>
                    <TitleSection>
                      <Title>สมุดสุขภาพ (Health check up report)</Title>
                      <Subtitle>แพทย์ผู้ตรวจ {getDoctorName(data)}</Subtitle>
                    </TitleSection>
                  </Header>

                  <PatientInfoSection>
                    <InfoRow>
                      <InfoLabel>
                        <strong>ประเภท/หน่วยงาน:</strong> {data.patientType}
                      </InfoLabel>
                      <InfoLabel>
                        <strong>HN:</strong> {data.hn}
                      </InfoLabel>
                    </InfoRow>
                    <InfoRow>
                      <InfoRowLeft>
                        <InfoLabel>
                          <strong>ชื่อ-นามสกุล:</strong> {data.patientName}
                        </InfoLabel>
                        <InfoLabel>
                          <strong>อายุ:</strong> {data.age} ปี
                        </InfoLabel>
                        <InfoLabel>
                          <strong>เพศ:</strong> {data.gender}
                        </InfoLabel>
                      </InfoRowLeft>
                      <InfoLabel>
                        <strong>ตรวจวันที่:</strong> {data.date}
                      </InfoLabel>
                    </InfoRow>
                  </PatientInfoSection>

                  <Divider />

                  <ContentSection>
                    <LeftSection>
                      <HumanImage src="/human.png" alt="Human Body" />
                    </LeftSection>

                    <RightSection>
                      <ResultHeader>ผลการตรวจสุขภาพร่างกายทั่วไป</ResultHeader>
                      <ResultTable>
                        <ResultRow>
                          <ResultLabel>น้ำหนัก (Weight)</ResultLabel>
                          <ResultValue>{data.weight}</ResultValue>
                        </ResultRow>
                        <ResultRow>
                          <ResultLabel>ส่วนสูง (Height)</ResultLabel>
                          <ResultValue>{data.height}</ResultValue>
                        </ResultRow>
                        <ResultRow>
                          <ResultLabel>ดัชนีมวลกาย (BMI)</ResultLabel>
                          <ResultValue>{data.bmi}</ResultValue>
                        </ResultRow>
                        <ResultRow>
                          <ResultLabel>• ผลการประเมิน BMI</ResultLabel>
                          <ResultValue>
                            <StatusBadge>{data.bmiStatus}</StatusBadge>
                          </ResultValue>
                        </ResultRow>
                        <ResultRow>
                          <ResultLabel>ความดันโลหิต (BP)</ResultLabel>
                          <ResultValue>{data.bloodPressure}</ResultValue>
                        </ResultRow>
                        <ResultRow>
                          <ResultLabel>ชีพจร (Pulse)</ResultLabel>
                          <ResultValue>{data.pulse}</ResultValue>
                        </ResultRow>
                        <ResultRowFull>
                          <ResultFullTitle>โรคประจำตัว</ResultFullTitle>
                          <ResultFullContent>
                            • {data.diseases}
                          </ResultFullContent>
                        </ResultRowFull>
                        <ResultRowFull>
                          <ResultFullTitle>
                            ประวัติแพ้ยา ,แพ้อาหาร
                          </ResultFullTitle>
                          <ResultFullContent>
                            • {data.foodAllergies}
                          </ResultFullContent>
                        </ResultRowFull>
                      </ResultTable>
                    </RightSection>
                  </ContentSection>

                  <AdditionalSection>
                    {/* สรุปความเสี่ยงสุขภาพ - แสดง tags */}
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_1.png" alt="Health Risk Icon" />
                        <InfoBoxTitle>สรุปความเสี่ยงสุขภาพ</InfoBoxTitle>
                      </InfoBoxHeader>
                      {data.tags && data.tags.length > 0 ? (
                        <ButtonGroup>
                          {data.tags.map((tag, index) => (
                            <RiskButton key={index}>{tag}</RiskButton>
                          ))}
                        </ButtonGroup>
                      ) : (
                        <InfoBoxContent
                          style={{ minHeight: "1.5rem" }}
                        ></InfoBoxContent>
                      )}
                    </InfoBox>

                    {/* คำแนะนำจากแพทย์ */}
                    {recordIsApproved &&
                      data.doctorNote &&
                      data.doctorNote !== "-" && (
                        <InfoBox>
                          <InfoBoxHeader>
                            <InfoIcon
                              src="/ic_2.png"
                              alt="Doctor Advice Icon"
                            />
                            <InfoBoxTitle>คำแนะนำจากแพทย์</InfoBoxTitle>
                          </InfoBoxHeader>
                          <InfoBoxContent style={{ whiteSpace: "pre-wrap" }}>
                            {data.doctorNote}
                          </InfoBoxContent>
                        </InfoBox>
                      )}
                  </AdditionalSection>

                  {/* PageBreak ก่อนหน้า 2 */}
                  <PageBreak />

                  {/* หน้าที่ 2 - คำแนะนำด้านโภชนาการ + การออกกำลังกาย */}
                  {renderPageHeader(2, data)}

                  <AdditionalSection>
                    {/* คำแนะนำด้านโภชนาการ */}
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_2.png" alt="Nutrition Advice Icon" />
                        <InfoBoxTitle>คำแนะนำด้านโภชนาการ</InfoBoxTitle>
                      </InfoBoxHeader>
                      <InfoBoxContent style={{ whiteSpace: "pre-wrap" }}>
                        {recordIsApproved ? data.nutritionAdvice || "" : ""}
                      </InfoBoxContent>
                    </InfoBox>

                    {/* คำแนะนำด้านการออกกำลังกาย */}
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_2.png" alt="Exercise Advice Icon" />
                        <InfoBoxTitle>คำแนะนำด้านการออกกำลังกาย</InfoBoxTitle>
                      </InfoBoxHeader>
                      <InfoBoxContent style={{ whiteSpace: "pre-wrap" }}>
                        {recordIsApproved ? data.exerciseAdvice || "" : ""}
                      </InfoBoxContent>
                    </InfoBox>
                  </AdditionalSection>

                  {/* PageBreak ก่อนหน้า 3 */}
                  <PageBreak />

                  {/* หน้าที่ 3 - คำแนะนำด้านการนอน */}
                  {renderPageHeader(3, data)}

                  <AdditionalSection>
                    {/* คำแนะนำด้านการนอน */}
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_2.png" alt="Sleep Advice Icon" />
                        <InfoBoxTitle>คำแนะนำด้านการนอน</InfoBoxTitle>
                      </InfoBoxHeader>
                      <InfoBoxContent style={{ whiteSpace: "pre-wrap" }}>
                        {recordIsApproved ? data.sleepAdvice || "" : ""}
                      </InfoBoxContent>
                    </InfoBox>
                  </AdditionalSection>

                  {/* แสดง PageBreak เฉพาะเมื่อมีข้อมูลพารามิเตอร์ (หน้า 4+) */}
                  {(hasPage2Data ||
                    hasPage3Data ||
                    hasPage4Data ||
                    hasAdditionalLabData) && (
                    <>
                      <PageBreak />

                      {/* หน้าที่ 4+ - พารามิเตอร์ผลตรวจทั้งหมด */}
                      {renderPageHeader(4, data)}

                      {/* การตรวจร่างกายทั่วไป */}
                      {(data.bmiValue !== "-" ||
                        data.bpsValue !== "-" ||
                        data.bpdValue !== "-" ||
                        data.pulseValue !== "-") && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>
                            การตรวจร่างกายทั่วไป
                          </BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.bmiValue !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>BMI</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.bmiValue,
                                    data.bmiNormal,
                                    data.bmiUnit,
                                  )}
                                >
                                  {data.bmiValue}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bmiNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bmiUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.bpsValue !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>ความดันบน (BPS)</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.bpsValue,
                                    data.bpsNormal,
                                    data.bpsUnit,
                                  )}
                                >
                                  {data.bpsValue}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bpsNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bpsUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.bpdValue !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>ความดันล่าง (BPD)</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.bpdValue,
                                    data.bpdNormal,
                                    data.bpdUnit,
                                  )}
                                >
                                  {data.bpdValue}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bpdNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bpdUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.pulseValue !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>ชีพจร (Pulse)</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.pulseValue,
                                    data.pulseNormal,
                                    data.pulseUnit,
                                  )}
                                >
                                  {data.pulseValue}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.pulseNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.pulseUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* กรุ๊ปเลือด */}
                      {data.bloodGroupValue !== "-" && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>กรุ๊ปเลือด</BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            <BloodTestRow>
                              <BloodTestCell>กรุ๊ปเลือด</BloodTestCell>
                              <BloodTestCell center>
                                {data.bloodGroupValue}
                              </BloodTestCell>
                              <BloodTestCell center>
                                {data.bloodGroupNormal}
                              </BloodTestCell>
                              <BloodTestCell center>
                                {data.bloodGroupUnit}
                              </BloodTestCell>
                            </BloodTestRow>
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* CBC Differential (%) section removed — these values now show in the main CBC table above */}
                      {false && (
                        <BloodTestTableWrapper>
                          <BloodTestContent>
                            {data.basophilPercent !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Basophil %</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.basophilPercent,
                                    data.basophilPercentNormal,
                                    data.basophilPercentUnit,
                                  )}
                                >
                                  {data.basophilPercent}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.basophilPercentNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.basophilPercentUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* ตารางน้ำตาลในเลือด (Glucose & HbA1C) */}
                      {(data.glucose !== "-" ||
                        data.nonNpoGlucose !== "-" ||
                        data.hba1c !== "-" ||
                        data.eag !== "-") && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>น้ำตาลในเลือด</BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.glucose !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  Fasting blood glucose
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.glucose,
                                    data.glucoseNormal,
                                    data.glucoseUnit,
                                  )}
                                >
                                  {data.glucose}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.glucoseNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.glucoseUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.nonNpoGlucose !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  Non-NPO blood glucose
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.nonNpoGlucose,
                                    data.nonNpoGlucoseNormal,
                                    data.nonNpoGlucoseUnit,
                                  )}
                                >
                                  {data.nonNpoGlucose}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.nonNpoGlucoseNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.nonNpoGlucoseUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.hba1c !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>HbA1C</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hba1c,
                                    data.hba1cNormal,
                                    data.hba1cUnit,
                                  )}
                                >
                                  {data.hba1c}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hba1cNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hba1cUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.eag !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>eAG</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.eag,
                                    data.eagNormal,
                                    data.eagUnit,
                                  )}
                                >
                                  {data.eag}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.eagNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.eagUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* ตารางความสมบูรณ์ของเม็ดเลือด */}
                      {(data.wbc !== "-" ||
                        data.hemoglobin !== "-" ||
                        data.hematocrit !== "-" ||
                        data.platelet !== "-" ||
                        data.neutrophile !== "-" ||
                        data.lymphocyte !== "-" ||
                        data.monocyte !== "-" ||
                        data.basophil !== "-" ||
                        data.eosinophil !== "-" ||
                        data.mcv !== "-" ||
                        data.mch !== "-" ||
                        data.mchc !== "-") && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>
                            ความสมบูรณ์ของเม็ดเลือด
                          </BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.wbc !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  เม็ดเลือดขาว (WBC)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.wbc,
                                    data.wbcNormal,
                                    data.wbcUnit,
                                  )}
                                >
                                  {data.wbc}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.wbcNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.wbcUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.hemoglobin !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  ฮีโมโกลบิน (Hemoglobin)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hemoglobin,
                                    data.hemoglobinNormal,
                                    data.hemoglobinUnit,
                                  )}
                                >
                                  {data.hemoglobin}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hemoglobinNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hemoglobinUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.hematocrit !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  ฮีมาโตคริต (Hematocrit)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hematocrit,
                                    data.hematocritNormal,
                                    data.hematocritUnit,
                                  )}
                                >
                                  {data.hematocrit}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hematocritNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hematocritUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.plateletsCount !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  เกล็ดเลือด (Platelets count)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.plateletsCount,
                                    data.plateletsCountNormal,
                                    data.plateletsCountUnit,
                                  )}
                                >
                                  {data.plateletsCount}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.plateletsCountNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.plateletsCountUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.platelet !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  เกล็ดเลือด (Platelet)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.platelet,
                                    data.plateletNormal,
                                    data.plateletUnit,
                                  )}
                                >
                                  {data.platelet}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.plateletNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.plateletUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.neutrophile !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  นิวโทรฟิล (Neutrophile)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.neutrophile,
                                    data.neutrophileNormal,
                                    data.neutrophileUnit,
                                  )}
                                >
                                  {data.neutrophile}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.neutrophileNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.neutrophileUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.lymphocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  ลิมโฟไซต์ (Lymphocyte)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.lymphocyte,
                                    data.lymphocyteNormal,
                                    data.lymphocyteUnit,
                                  )}
                                >
                                  {data.lymphocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.lymphocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.lymphocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.monocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  โมโนไซต์ (Monocyte)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.monocyte,
                                    data.monocyteNormal,
                                    data.monocyteUnit,
                                  )}
                                >
                                  {data.monocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.monocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.monocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.basophil !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  เบโซฟิล (Basophil)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.basophil,
                                    data.basophilNormal,
                                    data.basophilUnit,
                                  )}
                                >
                                  {data.basophil}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.basophilNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.basophilUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.eosinophil !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  อีโอซิโนฟิล (Eosinophil)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.eosinophil,
                                    data.eosinophilNormal,
                                    data.eosinophilUnit,
                                  )}
                                >
                                  {data.eosinophil}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.eosinophilNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.eosinophilUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.mcv !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>MCV</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.mcv,
                                    data.mcvNormal,
                                    data.mcvUnit,
                                  )}
                                >
                                  {data.mcv}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.mcvNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.mcvUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.mch !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>MCH</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.mch,
                                    data.mchNormal,
                                    data.mchUnit,
                                  )}
                                >
                                  {data.mch}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.mchNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.mchUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.mchc !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>MCHC</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.mchc,
                                    data.mchcNormal,
                                    data.mchcUnit,
                                  )}
                                >
                                  {data.mchc}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.mchcNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.mchcUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.nrbc !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>NRBC</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.nrbc,
                                    data.nrbcNormal,
                                    data.nrbcUnit,
                                  )}
                                >
                                  {data.nrbc}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.nrbcNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.nrbcUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.myeloblast !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Myeloblast</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.myeloblast,
                                    data.myeloblastNormal,
                                    data.myeloblastUnit,
                                  )}
                                >
                                  {data.myeloblast}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.myeloblastNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.myeloblastUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.promyelocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Promyelocyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.promyelocyte,
                                    data.promyelocyteNormal,
                                    data.promyelocyteUnit,
                                  )}
                                >
                                  {data.promyelocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.promyelocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.promyelocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.myelocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Myelocyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.myelocyte,
                                    data.myelocyteNormal,
                                    data.myelocyteUnit,
                                  )}
                                >
                                  {data.myelocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.myelocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.myelocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.metamyelocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Metamyelocyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.metamyelocyte,
                                    data.metamyelocyteNormal,
                                    data.metamyelocyteUnit,
                                  )}
                                >
                                  {data.metamyelocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.metamyelocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.metamyelocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.lymphoblast !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Lymphoblast</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.lymphoblast,
                                    data.lymphoblastNormal,
                                    data.lymphoblastUnit,
                                  )}
                                >
                                  {data.lymphoblast}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.lymphoblastNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.lymphoblastUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.bandForm !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Band form</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.bandForm,
                                    data.bandFormNormal,
                                    data.bandFormUnit,
                                  )}
                                >
                                  {data.bandForm}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bandFormNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bandFormUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.anisocytosis !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Anisocytosis</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.anisocytosis,
                                    data.anisocytosisNormal,
                                    data.anisocytosisUnit,
                                  )}
                                >
                                  {data.anisocytosis}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.anisocytosisNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.anisocytosisUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.macrocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Macrocyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.macrocyte,
                                    data.macrocyteNormal,
                                    data.macrocyteUnit,
                                  )}
                                >
                                  {data.macrocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.macrocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.macrocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.microcyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Microcyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.microcyte,
                                    data.microcyteNormal,
                                    data.microcyteUnit,
                                  )}
                                >
                                  {data.microcyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.microcyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.microcyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.poikilocytosis !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Poikilocytosis</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.poikilocytosis,
                                    data.poikilocytosisNormal,
                                    data.poikilocytosisUnit,
                                  )}
                                >
                                  {data.poikilocytosis}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.poikilocytosisNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.poikilocytosisUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.ovalocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Ovalocyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.ovalocyte,
                                    data.ovalocyteNormal,
                                    data.ovalocyteUnit,
                                  )}
                                >
                                  {data.ovalocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.ovalocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.ovalocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.targetCell !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Target cell</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.targetCell,
                                    data.targetCellNormal,
                                    data.targetCellUnit,
                                  )}
                                >
                                  {data.targetCell}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.targetCellNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.targetCellUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.schistocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Schistocyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.schistocyte,
                                    data.schistocyteNormal,
                                    data.schistocyteUnit,
                                  )}
                                >
                                  {data.schistocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.schistocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.schistocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.spherocyte !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Spherocyte</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.spherocyte,
                                    data.spherocyteNormal,
                                    data.spherocyteUnit,
                                  )}
                                >
                                  {data.spherocyte}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.spherocyteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.spherocyteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.tearDropCell !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Tear drop cell</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.tearDropCell,
                                    data.tearDropCellNormal,
                                    data.tearDropCellUnit,
                                  )}
                                >
                                  {data.tearDropCell}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.tearDropCellNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.tearDropCellUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.burrCell !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Burr cell</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.burrCell,
                                    data.burrCellNormal,
                                    data.burrCellUnit,
                                  )}
                                >
                                  {data.burrCell}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.burrCellNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.burrCellUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.polychromasia !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Polychromasia</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.polychromasia,
                                    data.polychromasiaNormal,
                                    data.polychromasiaUnit,
                                  )}
                                >
                                  {data.polychromasia}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.polychromasiaNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.polychromasiaUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.blastCell !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Blast cell</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.blastCell,
                                    data.blastCellNormal,
                                    data.blastCellUnit,
                                  )}
                                >
                                  {data.blastCell}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.blastCellNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.blastCellUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {(data.hypochromic !== "-" ||
                              data.microcyte !== "-" ||
                              data.rbcMorphology !== "-") && (
                              <>
                                {data.hypochromic !== "-" && (
                                  <BloodTestRow>
                                    <BloodTestCell>Hypochromic</BloodTestCell>
                                    <BloodTestCell
                                      center
                                      abnormal={isAbnormal(
                                        data.hypochromic,
                                        data.hypochromicNormal,
                                        data.hypochromicUnit,
                                      )}
                                    >
                                      {data.hypochromic}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.hypochromicNormal}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.hypochromicUnit}
                                    </BloodTestCell>
                                  </BloodTestRow>
                                )}
                                {data.rbcMorphology !== "-" && (
                                  <BloodTestRow>
                                    <BloodTestCell>
                                      RBC morphology
                                    </BloodTestCell>
                                    <BloodTestCell
                                      center
                                      abnormal={isAbnormal(
                                        data.rbcMorphology,
                                        data.rbcMorphologyNormal,
                                        data.rbcMorphologyUnit,
                                      )}
                                    >
                                      {data.rbcMorphology}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.rbcMorphologyNormal}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.rbcMorphologyUnit}
                                    </BloodTestCell>
                                  </BloodTestRow>
                                )}
                              </>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* ตารางไขมันในเลือด */}
                      {/* CBC Morphology / Hidden Differential */}
                      {false &&
                        (data.nrbc !== "-" ||
                          data.myeloblast !== "-" ||
                          data.promyelocyte !== "-" ||
                          data.myelocyte !== "-" ||
                          data.metamyelocyte !== "-" ||
                          data.lymphoblast !== "-" ||
                          data.bandForm !== "-" ||
                          data.anisocytosis !== "-" ||
                          data.macrocyte !== "-" ||
                          data.poikilocytosis !== "-" ||
                          data.ovalocyte !== "-" ||
                          data.targetCell !== "-" ||
                          data.schistocyte !== "-" ||
                          data.spherocyte !== "-" ||
                          data.tearDropCell !== "-" ||
                          data.burrCell !== "-" ||
                          data.polychromasia !== "-" ||
                          data.blastCell !== "-" ||
                          data.rbcMorphology !== "-") && (
                          <BloodTestTableWrapper>
                            <BloodTestHeader>
                              CBC Morphology / Hidden Differential
                            </BloodTestHeader>
                            <BloodTestContent>
                              <BloodTestRow>
                                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                                <BloodTestCell header center>
                                  ผลการตรวจ
                                </BloodTestCell>
                                <BloodTestCell header center>
                                  ค่าปกติ
                                </BloodTestCell>
                                <BloodTestCell header center>
                                  หน่วย
                                </BloodTestCell>
                              </BloodTestRow>
                              {data.nrbc !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>NRBC</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.nrbc}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.nrbcNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.nrbcUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.myeloblast !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Myeloblast</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.myeloblast}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.myeloblastNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.myeloblastUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.promyelocyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Promyelocyte</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.promyelocyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.promyelocyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.promyelocyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.myelocyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Myelocyte</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.myelocyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.myelocyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.myelocyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.metamyelocyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Metamyelocyte</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.metamyelocyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.metamyelocyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.metamyelocyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.lymphoblast !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Lymphoblast</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.lymphoblast}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.lymphoblastNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.lymphoblastUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.bandForm !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Band form</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.bandForm}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.bandFormNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.bandFormUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.anisocytosis !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Anisocytosis</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.anisocytosis}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.anisocytosisNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.anisocytosisUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.macrocyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Macrocyte</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.macrocyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.macrocyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.macrocyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.microcyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Microcyte</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.microcyte,
                                      data.microcyteNormal,
                                      data.microcyteUnit,
                                    )}
                                  >
                                    {data.microcyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.microcyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.microcyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.poikilocytosis !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Poikilocytosis</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.poikilocytosis}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.poikilocytosisNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.poikilocytosisUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.ovalocyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Ovalocyte</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.ovalocyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.ovalocyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.ovalocyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.targetCell !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Target cell</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.targetCell}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.targetCellNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.targetCellUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.schistocyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Schistocyte</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.schistocyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.schistocyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.schistocyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.spherocyte !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Spherocyte</BloodTestCell>
                                  <BloodTestCell center>
                                    {data.spherocyte}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.spherocyteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.spherocyteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.tearDropCell !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Tear drop cell</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.tearDropCell,
                                      data.tearDropCellNormal,
                                      data.tearDropCellUnit,
                                    )}
                                  >
                                    {data.tearDropCell}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.tearDropCellNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.tearDropCellUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.burrCell !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Burr cell</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.burrCell,
                                      data.burrCellNormal,
                                      data.burrCellUnit,
                                    )}
                                  >
                                    {data.burrCell}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.burrCellNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.burrCellUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.polychromasia !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Polychromasia</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.polychromasia,
                                      data.polychromasiaNormal,
                                      data.polychromasiaUnit,
                                    )}
                                  >
                                    {data.polychromasia}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.polychromasiaNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.polychromasiaUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.blastCell !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Blast cell</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.blastCell,
                                      data.blastCellNormal,
                                      data.blastCellUnit,
                                    )}
                                  >
                                    {data.blastCell}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.blastCellNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.blastCellUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {(data.hypochromic !== "-" ||
                                data.microcyte !== "-" ||
                                data.rbcMorphology !== "-") && (
                                <>
                                  {data.hypochromic !== "-" && (
                                    <BloodTestRow>
                                      <BloodTestCell>Hypochromic</BloodTestCell>
                                      <BloodTestCell
                                        center
                                        abnormal={isAbnormal(
                                          data.hypochromic,
                                          data.hypochromicNormal,
                                          data.hypochromicUnit,
                                        )}
                                      >
                                        {data.hypochromic}
                                      </BloodTestCell>
                                      <BloodTestCell center>
                                        {data.hypochromicNormal}
                                      </BloodTestCell>
                                      <BloodTestCell center>
                                        {data.hypochromicUnit}
                                      </BloodTestCell>
                                    </BloodTestRow>
                                  )}
                                  {data.rbcMorphology !== "-" && (
                                    <BloodTestRow>
                                      <BloodTestCell>
                                        RBC morphology
                                      </BloodTestCell>
                                      <BloodTestCell
                                        center
                                        abnormal={isAbnormal(
                                          data.rbcMorphology,
                                          data.rbcMorphologyNormal,
                                          data.rbcMorphologyUnit,
                                        )}
                                      >
                                        {data.rbcMorphology}
                                      </BloodTestCell>
                                      <BloodTestCell center>
                                        {data.rbcMorphologyNormal}
                                      </BloodTestCell>
                                      <BloodTestCell center>
                                        {data.rbcMorphologyUnit}
                                      </BloodTestCell>
                                    </BloodTestRow>
                                  )}
                                </>
                              )}
                            </BloodTestContent>
                          </BloodTestTableWrapper>
                        )}

                      {(data.totalCholesterol !== "-" ||
                        data.triglyceride !== "-" ||
                        data.hdl !== "-" ||
                        data.ldl !== "-") && (
                        <>
                          <PageBreak />
                          {renderPageHeader(5, data)}
                          <BloodTestTableWrapper>
                            <BloodTestHeader>ไขมันในเลือด</BloodTestHeader>
                            <BloodTestContent>
                              <BloodTestRow>
                                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                                <BloodTestCell header center>
                                  ผลการตรวจ
                                </BloodTestCell>
                                <BloodTestCell header center>
                                  ค่าปกติ
                                </BloodTestCell>
                                <BloodTestCell header center>
                                  หน่วย
                                </BloodTestCell>
                              </BloodTestRow>
                              {data.totalCholesterol !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Cholesterol</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.totalCholesterol,
                                      data.totalCholesterolNormal,
                                      data.totalCholesterolUnit,
                                    )}
                                  >
                                    {data.totalCholesterol}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.totalCholesterolNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.totalCholesterolUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.triglyceride !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Triglyceride</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.triglyceride,
                                      data.triglycerideNormal,
                                      data.triglycerideUnit,
                                    )}
                                  >
                                    {data.triglyceride}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.triglycerideNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.triglycerideUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.hdl !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>HDL</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.hdl,
                                      data.hdlNormal,
                                      data.hdlUnit,
                                    )}
                                  >
                                    {data.hdl}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.hdlNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.hdlUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.ldl !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>LDL</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.ldl,
                                      data.ldlNormal,
                                      data.ldlUnit,
                                    )}
                                  >
                                    {data.ldl}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.ldlNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.ldlUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                            </BloodTestContent>
                          </BloodTestTableWrapper>
                        </>
                      )}

                      {/* ตารางการทำงานของไต */}
                      {(data.creatinine !== "-" ||
                        data.bun !== "-" ||
                        data.egfr !== "-") && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>การทำงานของไต</BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.creatinine !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Creatinine</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.creatinine,
                                    data.creatinineNormal,
                                    data.creatinineUnit,
                                  )}
                                >
                                  {data.creatinine}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.creatinineNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.creatinineUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.bun !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>BUN , Urea</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.bun,
                                    data.bunNormal,
                                    data.bunUnit,
                                  )}
                                >
                                  {data.bun}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bunNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.bunUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.egfr !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>eGFR</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.egfr,
                                    data.egfrNormal,
                                    data.egfrUnit,
                                  )}
                                >
                                  {data.egfr}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.egfrNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.egfrUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* ตารางการทำงานของตับ */}
                      {(data.sgpt !== "-" ||
                        data.sgot !== "-" ||
                        data.alp !== "-" ||
                        data.totalBilirubin !== "-" ||
                        data.albumin !== "-" ||
                        data.totalProtein !== "-" ||
                        data.ggt !== "-" ||
                        data.globulin !== "-" ||
                        data.directBilirubin !== "-") && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>การทำงานของตับ</BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.sgpt !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>SGPT</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.sgpt,
                                    data.sgptNormal,
                                    data.sgptUnit,
                                  )}
                                >
                                  {data.sgpt}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.sgptNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.sgptUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.sgot !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>SGOT</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.sgot,
                                    data.sgotNormal,
                                    data.sgotUnit,
                                  )}
                                >
                                  {data.sgot}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.sgotNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.sgotUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.alp !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  Alkaline Phosphatase
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.alp,
                                    data.alpNormal,
                                    data.alpUnit,
                                  )}
                                >
                                  {data.alp}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.alpNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.alpUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.totalBilirubin !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Total Bilirubin</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.totalBilirubin,
                                    data.totalBilirubinNormal,
                                    data.totalBilirubinUnit,
                                  )}
                                >
                                  {data.totalBilirubin}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.totalBilirubinNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.totalBilirubinUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.albumin !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  อัลบูมิน (Albumin)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.albumin,
                                    data.albuminNormal,
                                    data.albuminUnit,
                                  )}
                                >
                                  {data.albumin}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.albuminNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.albuminUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.totalProtein !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  โปรตีนรวม (Total Protein)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.totalProtein,
                                    data.totalProteinNormal,
                                    data.totalProteinUnit,
                                  )}
                                >
                                  {data.totalProtein}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.totalProteinNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.totalProteinUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.ggt !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  GGT (Gamma-glutamyl transferase)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.ggt,
                                    data.ggtNormal,
                                    data.ggtUnit,
                                  )}
                                >
                                  {data.ggt}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.ggtNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.ggtUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.globulin !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Globulin</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.globulin,
                                    data.globulinNormal,
                                    data.globulinUnit,
                                  )}
                                >
                                  {data.globulin}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.globulinNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.globulinUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.directBilirubin !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Direct Bilirubin</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.directBilirubin,
                                    data.directBilirubinNormal,
                                    data.directBilirubinUnit,
                                  )}
                                >
                                  {data.directBilirubin}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.directBilirubinNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.directBilirubinUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* ตารางกรดยูริค */}
                      {data.uricAcid !== "-" && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>กรดยูริคในเลือด</BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            <BloodTestRow>
                              <BloodTestCell>Uric Acid</BloodTestCell>
                              <BloodTestCell
                                center
                                abnormal={isAbnormal(
                                  data.uricAcid,
                                  data.uricAcidNormal,
                                  data.uricAcidUnit,
                                )}
                              >
                                {data.uricAcid}
                              </BloodTestCell>
                              <BloodTestCell center>
                                {data.uricAcidNormal}
                              </BloodTestCell>
                              <BloodTestCell center>
                                {data.uricAcidUnit}
                              </BloodTestCell>
                            </BloodTestRow>
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}
                      {/* ตารางผลการตรวจปัสสาวะ */}
                      {(data.urineColor !== "-" ||
                        data.urineClarity !== "-" ||
                        data.urineAppearance !== "-" ||
                        data.urinePh !== "-" ||
                        data.urineSpecificGravity !== "-" ||
                        data.urineProtein !== "-" ||
                        data.urineSugar !== "-" ||
                        data.urineKetones !== "-" ||
                        data.urineBlood !== "-" ||
                        data.urineWbc !== "-" ||
                        data.urineNitrite !== "-" ||
                        data.urineBilirubin !== "-" ||
                        data.urineUrobilinogen !== "-" ||
                        data.urineRbc !== "-" ||
                        data.urineWbcMicroscopic !== "-" ||
                        data.urineBacteria !== "-" ||
                        data.urineEpithelial !== "-" ||
                        data.urineOthers !== "-" ||
                        data.mucusThread !== "-" ||
                        data.urineNonSquamousEpithelialCell !== "-" ||
                        data.urineRenalTubularEpithelialCell !== "-") && (
                        <>
                          <PageBreak />
                          {renderPageHeader(6, data)}
                          <BloodTestTableWrapper>
                            <BloodTestHeader>ผลการตรวจปัสสาวะ</BloodTestHeader>
                            <BloodTestContent>
                              <BloodTestRow>
                                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                                <BloodTestCell header center>
                                  ผลการตรวจ
                                </BloodTestCell>
                                <BloodTestCell header center>
                                  ค่าปกติ
                                </BloodTestCell>
                                <BloodTestCell header center>
                                  หน่วย
                                </BloodTestCell>
                              </BloodTestRow>
                              {data.urineColor !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>สีของปัสสาวะ</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineColor,
                                      data.urineColorNormal,
                                      data.urineColorUnit,
                                    )}
                                  >
                                    {data.urineColor}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineColorNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineColorUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineClarity !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    ความใส (Clarity)
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineClarity,
                                      data.urineClarityNormal,
                                      data.urineClarityUnit,
                                    )}
                                  >
                                    {data.urineClarity}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineClarityNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineClarityUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineAppearance !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Urine Appearance
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineAppearance,
                                      data.urineAppearanceNormal,
                                      data.urineAppearanceUnit,
                                    )}
                                  >
                                    {data.urineAppearance}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineAppearanceNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineAppearanceUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urinePh !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>pH</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urinePh,
                                      data.urinePhNormal,
                                      data.urinePhUnit,
                                    )}
                                  >
                                    {data.urinePh}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urinePhNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urinePhUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineSpecificGravity !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>ความถ่วงจำเพาะ</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineSpecificGravity,
                                      data.urineSpecificGravityNormal,
                                      data.urineSpecificGravityUnit,
                                    )}
                                  >
                                    {data.urineSpecificGravity}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineSpecificGravityNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineSpecificGravityUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineProtein !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>โปรตีน</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineProtein,
                                      data.urineProteinNormal,
                                      data.urineProteinUnit,
                                    )}
                                  >
                                    {data.urineProtein}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineProteinNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineProteinUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineSugar !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>น้ำตาล</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineSugar,
                                      data.urineSugarNormal,
                                      data.urineSugarUnit,
                                    )}
                                  >
                                    {data.urineSugar}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineSugarNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineSugarUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineKetones !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>คีโตน</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineKetones,
                                      data.urineKetonesNormal,
                                      data.urineKetonesUnit,
                                    )}
                                  >
                                    {data.urineKetones}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineKetonesNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineKetonesUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineBlood !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>เลือด</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineBlood,
                                      data.urineBloodNormal,
                                      data.urineBloodUnit,
                                    )}
                                  >
                                    {data.urineBlood}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineBloodNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineBloodUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineWbc !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>เม็ดเลือดขาว</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineWbc,
                                      data.urineWbcNormal,
                                      data.urineWbcUnit,
                                    )}
                                  >
                                    {data.urineWbc}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineWbcNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineWbcUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineNitrite !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>ไนเตรต</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineNitrite,
                                      data.urineNitriteNormal,
                                      data.urineNitriteUnit,
                                    )}
                                  >
                                    {data.urineNitrite}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineNitriteNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineNitriteUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineBilirubin !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>บิลิรูบิน</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineBilirubin,
                                      data.urineBilirubinNormal,
                                      data.urineBilirubinUnit,
                                    )}
                                  >
                                    {data.urineBilirubin}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineBilirubinNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineBilirubinUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineUrobilinogen !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>ยูโรบิลินโนเจน</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineUrobilinogen,
                                      data.urineUrobilinogenNormal,
                                      data.urineUrobilinogenUnit,
                                    )}
                                  >
                                    {data.urineUrobilinogen}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineUrobilinogenNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineUrobilinogenUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineRbc !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    เซลล์เม็ดเลือดแดง
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineRbc,
                                      data.urineRbcNormal,
                                      data.urineRbcUnit,
                                    )}
                                  >
                                    {data.urineRbc}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineRbcNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineRbcUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineWbcMicroscopic !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    เซลล์เม็ดเลือดขาว
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineWbcMicroscopic,
                                      data.urineWbcMicroscopicNormal,
                                      data.urineWbcMicroscopicUnit,
                                    )}
                                  >
                                    {data.urineWbcMicroscopic}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineWbcMicroscopicNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineWbcMicroscopicUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineBacteria !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>แบคทีเรีย</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineBacteria,
                                      data.urineBacteriaNormal,
                                      data.urineBacteriaUnit,
                                    )}
                                  >
                                    {data.urineBacteria}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineBacteriaNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineBacteriaUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineOthers !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Urinalysis Others
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineOthers,
                                      data.urineOthersNormal,
                                      data.urineOthersUnit,
                                    )}
                                  >
                                    {data.urineOthers}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineOthersNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineOthersUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.mucusThread !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Mucus thread</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.mucusThread,
                                      data.mucusThreadNormal,
                                      data.mucusThreadUnit,
                                    )}
                                  >
                                    {data.mucusThread}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.mucusThreadNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.mucusThreadUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineEpithelial !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>เซลล์เยื่อบุผิว</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineEpithelial,
                                      data.urineEpithelialNormal,
                                      data.urineEpithelialUnit,
                                    )}
                                  >
                                    {data.urineEpithelial}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineEpithelialNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineEpithelialUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineNonSquamousEpithelialCell !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Non-squamous epithelial cell
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineNonSquamousEpithelialCell,
                                      data.urineNonSquamousEpithelialCellNormal,
                                      data.urineNonSquamousEpithelialCellUnit,
                                    )}
                                  >
                                    {data.urineNonSquamousEpithelialCell}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineNonSquamousEpithelialCellNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineNonSquamousEpithelialCellUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineRenalTubularEpithelialCell !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Renal tubular epithelial cell
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineRenalTubularEpithelialCell,
                                      data.urineRenalTubularEpithelialCellNormal,
                                      data.urineRenalTubularEpithelialCellUnit,
                                    )}
                                  >
                                    {data.urineRenalTubularEpithelialCell}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineRenalTubularEpithelialCellNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineRenalTubularEpithelialCellUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineCalciumOxalate !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Calcium Oxalate</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineCalciumOxalate,
                                      data.urineCalciumOxalateNormal,
                                      data.urineCalciumOxalateUnit,
                                    )}
                                  >
                                    {data.urineCalciumOxalate}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCalciumOxalateNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCalciumOxalateUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineCalciumCarbonate !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Calcium Carbonate crystal
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineCalciumCarbonate,
                                      data.urineCalciumCarbonateNormal,
                                      data.urineCalciumCarbonateUnit,
                                    )}
                                  >
                                    {data.urineCalciumCarbonate}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCalciumCarbonateNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCalciumCarbonateUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineUricAcidCrystal !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Uric Acid Crystal
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineUricAcidCrystal,
                                      data.urineUricAcidCrystalNormal,
                                      data.urineUricAcidCrystalUnit,
                                    )}
                                  >
                                    {data.urineUricAcidCrystal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineUricAcidCrystalNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineUricAcidCrystalUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineTriplePhosphate !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Triple Phosphate
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineTriplePhosphate,
                                      data.urineTriplePhosphateNormal,
                                      data.urineTriplePhosphateUnit,
                                    )}
                                  >
                                    {data.urineTriplePhosphate}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineTriplePhosphateNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineTriplePhosphateUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineCalciumPhosphate !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>
                                    Calcium phosphate crystal
                                  </BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineCalciumPhosphate,
                                      data.urineCalciumPhosphateNormal,
                                      data.urineCalciumPhosphateUnit,
                                    )}
                                  >
                                    {data.urineCalciumPhosphate}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCalciumPhosphateNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCalciumPhosphateUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineCystineCrystal !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Cystine crystal</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineCystineCrystal,
                                      data.urineCystineCrystalNormal,
                                      data.urineCystineCrystalUnit,
                                    )}
                                  >
                                    {data.urineCystineCrystal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCystineCrystalNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCystineCrystalUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineHyalineCast !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Hyaline Cast</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineHyalineCast,
                                      data.urineHyalineCastNormal,
                                      data.urineHyalineCastUnit,
                                    )}
                                  >
                                    {data.urineHyalineCast}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineHyalineCastNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineHyalineCastUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineGranularCast !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Granular Cast</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineGranularCast,
                                      data.urineGranularCastNormal,
                                      data.urineGranularCastUnit,
                                    )}
                                  >
                                    {data.urineGranularCast}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineGranularCastNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineGranularCastUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineCellularCast !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Cellular Cast</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineCellularCast,
                                      data.urineCellularCastNormal,
                                      data.urineCellularCastUnit,
                                    )}
                                  >
                                    {data.urineCellularCast}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCellularCastNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineCellularCastUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineWaxyCast !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>Waxy Cast</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineWaxyCast,
                                      data.urineWaxyCastNormal,
                                      data.urineWaxyCastUnit,
                                    )}
                                  >
                                    {data.urineWaxyCast}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineWaxyCastNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineWaxyCastUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                              {data.urineRbcCast !== "-" && (
                                <BloodTestRow>
                                  <BloodTestCell>RBC cast</BloodTestCell>
                                  <BloodTestCell
                                    center
                                    abnormal={isAbnormal(
                                      data.urineRbcCast,
                                      data.urineRbcCastNormal,
                                      data.urineRbcCastUnit,
                                    )}
                                  >
                                    {data.urineRbcCast}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineRbcCastNormal}
                                  </BloodTestCell>
                                  <BloodTestCell center>
                                    {data.urineRbcCastUnit}
                                  </BloodTestCell>
                                </BloodTestRow>
                              )}
                            </BloodTestContent>
                          </BloodTestTableWrapper>
                        </>
                      )}

                      {/* ตารางผลการตรวจ Urinalysis Casts */}
                      {false && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>
                            ผลการตรวจ Urine Casts
                          </BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.urineHyalineCast !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Hyaline Cast</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.urineHyalineCast,
                                    data.urineHyalineCastNormal,
                                    data.urineHyalineCastUnit,
                                  )}
                                >
                                  {data.urineHyalineCast}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineHyalineCastNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineHyalineCastUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.urineGranularCast !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Granular Cast</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.urineGranularCast,
                                    data.urineGranularCastNormal,
                                    data.urineGranularCastUnit,
                                  )}
                                >
                                  {data.urineGranularCast}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineGranularCastNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineGranularCastUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.urineCellularCast !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Cellular Cast</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.urineCellularCast,
                                    data.urineCellularCastNormal,
                                    data.urineCellularCastUnit,
                                  )}
                                >
                                  {data.urineCellularCast}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineCellularCastNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineCellularCastUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.urineWaxyCast !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>Waxy Cast</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.urineWaxyCast,
                                    data.urineWaxyCastNormal,
                                    data.urineWaxyCastUnit,
                                  )}
                                >
                                  {data.urineWaxyCast}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineWaxyCastNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineWaxyCastUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.urineRbcCast !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>RBC cast</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.urineRbcCast,
                                    data.urineRbcCastNormal,
                                    data.urineRbcCastUnit,
                                  )}
                                >
                                  {data.urineRbcCast}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineRbcCastNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.urineRbcCastUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* ตารางผลการตรวจ Urinalysis Crystals */}
                      {false &&
                        (data.urineCalciumOxalate !== "-" ||
                          data.urineCalciumCarbonate !== "-" ||
                          data.urineUricAcidCrystal !== "-" ||
                          data.urineTriplePhosphate !== "-" ||
                          data.urineCalciumPhosphate !== "-" ||
                          data.urineCystineCrystal !== "-") && (
                          <>
                            <PageBreak />
                            {renderPageHeader(7, data)}
                            <BloodTestTableWrapper>
                              <BloodTestHeader>
                                ผลการตรวจ Urine Crystals
                              </BloodTestHeader>
                              <BloodTestContent>
                                <BloodTestRow>
                                  <BloodTestCell header>
                                    รายการตรวจ
                                  </BloodTestCell>
                                  <BloodTestCell header center>
                                    ผลการตรวจ
                                  </BloodTestCell>
                                  <BloodTestCell header center>
                                    ค่าปกติ
                                  </BloodTestCell>
                                  <BloodTestCell header center>
                                    หน่วย
                                  </BloodTestCell>
                                </BloodTestRow>
                                {data.urineCalciumOxalate !== "-" && (
                                  <BloodTestRow>
                                    <BloodTestCell>
                                      Calcium Oxalate
                                    </BloodTestCell>
                                    <BloodTestCell
                                      center
                                      abnormal={isAbnormal(
                                        data.urineCalciumOxalate,
                                        data.urineCalciumOxalateNormal,
                                        data.urineCalciumOxalateUnit,
                                      )}
                                    >
                                      {data.urineCalciumOxalate}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineCalciumOxalateNormal}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineCalciumOxalateUnit}
                                    </BloodTestCell>
                                  </BloodTestRow>
                                )}
                                {data.urineCalciumCarbonate !== "-" && (
                                  <BloodTestRow>
                                    <BloodTestCell>
                                      Calcium Carbonate crystal
                                    </BloodTestCell>
                                    <BloodTestCell
                                      center
                                      abnormal={isAbnormal(
                                        data.urineCalciumCarbonate,
                                        data.urineCalciumCarbonateNormal,
                                        data.urineCalciumCarbonateUnit,
                                      )}
                                    >
                                      {data.urineCalciumCarbonate}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineCalciumCarbonateNormal}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineCalciumCarbonateUnit}
                                    </BloodTestCell>
                                  </BloodTestRow>
                                )}
                                {data.urineUricAcidCrystal !== "-" && (
                                  <BloodTestRow>
                                    <BloodTestCell>
                                      Uric Acid Crystal
                                    </BloodTestCell>
                                    <BloodTestCell
                                      center
                                      abnormal={isAbnormal(
                                        data.urineUricAcidCrystal,
                                        data.urineUricAcidCrystalNormal,
                                        data.urineUricAcidCrystalUnit,
                                      )}
                                    >
                                      {data.urineUricAcidCrystal}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineUricAcidCrystalNormal}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineUricAcidCrystalUnit}
                                    </BloodTestCell>
                                  </BloodTestRow>
                                )}
                                {data.urineTriplePhosphate !== "-" && (
                                  <BloodTestRow>
                                    <BloodTestCell>
                                      Triple Phosphate
                                    </BloodTestCell>
                                    <BloodTestCell
                                      center
                                      abnormal={isAbnormal(
                                        data.urineTriplePhosphate,
                                        data.urineTriplePhosphateNormal,
                                        data.urineTriplePhosphateUnit,
                                      )}
                                    >
                                      {data.urineTriplePhosphate}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineTriplePhosphateNormal}
                                    </BloodTestCell>
                                    <BloodTestCell center>
                                      {data.urineTriplePhosphateUnit}
                                    </BloodTestCell>
                                  </BloodTestRow>
                                )}
                              </BloodTestContent>
                            </BloodTestTableWrapper>
                          </>
                        )}

                      {/* ตารางผลการตรวจอุจจาระ */}
                      {(data.stoolWbc !== "-" ||
                        data.stoolRbc !== "-" ||
                        data.parasite !== "-" ||
                        data.stoolColor !== "-" ||
                        data.stoolConsistency !== "-") && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>ผลการตรวจอุจจาระ</BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.stoolColor !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>สีของอุจจาระ</BloodTestCell>
                                <BloodTestCell center abnormal={false}>
                                  {data.stoolColor}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolColorNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolColorUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.stoolWbc !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  เม็ดเลือดขาว (WBC)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.stoolWbc,
                                    data.stoolWbcNormal,
                                    data.stoolWbcUnit,
                                  )}
                                >
                                  {data.stoolWbc}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolWbcNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolWbcUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.stoolRbc !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  เม็ดเลือดแดง (RBC)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.stoolRbc,
                                    data.stoolRbcNormal,
                                    data.stoolRbcUnit,
                                  )}
                                >
                                  {data.stoolRbc}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolRbcNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolRbcUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.parasite !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  พยาธิ, ไข่พยาธิ (Parasite, Ova)
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.parasite,
                                    data.parasiteNormal,
                                    data.parasiteUnit,
                                  )}
                                >
                                  {data.parasite}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.parasiteNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.parasiteUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.stoolConsistency !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  ลักษณะ (Consistency)
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolConsistency}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolConsistencyNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.stoolConsistencyUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}

                      {/* ตารางผลการตรวจ Hepatitis Markers */}
                      {(data.hbsag !== "-" ||
                        data.antiHbs !== "-" ||
                        data.hcvAb !== "-" ||
                        data.antiHbsConcentration !== "-" ||
                        data.hbsagConcentration !== "-" ||
                        data.hbsagMethodology !== "-" ||
                        data.hcvAbMethodology !== "-" ||
                        data.coi !== "-") && (
                        <BloodTestTableWrapper>
                          <BloodTestHeader>
                            ผลการตรวจไวรัสตับอักเสบ
                          </BloodTestHeader>
                          <BloodTestContent>
                            <BloodTestRow>
                              <BloodTestCell header>รายการตรวจ</BloodTestCell>
                              <BloodTestCell header center>
                                ผลการตรวจ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                ค่าปกติ
                              </BloodTestCell>
                              <BloodTestCell header center>
                                หน่วย
                              </BloodTestCell>
                            </BloodTestRow>
                            {data.hbsag !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>HBsAg</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hbsag,
                                    data.hbsagNormal,
                                    data.hbsagUnit,
                                  )}
                                >
                                  {data.hbsag}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hbsagNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hbsagUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.antiHbs !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>HBsAb</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.antiHbs,
                                    data.antiHbsNormal,
                                    data.antiHbsUnit,
                                  )}
                                >
                                  {data.antiHbs}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.antiHbsNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.antiHbsUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.antiHbsConcentration !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  Anti-HBs Concentration
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.antiHbsConcentration,
                                    data.antiHbsConcentrationNormal,
                                    data.antiHbsConcentrationUnit,
                                  )}
                                >
                                  {data.antiHbsConcentration}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.antiHbsConcentrationNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.antiHbsConcentrationUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.hcvAb !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>HCV Ab</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hcvAb,
                                    data.hcvAbNormal,
                                    data.hcvAbUnit,
                                  )}
                                >
                                  {data.hcvAb}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hcvAbNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hcvAbUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.hbsagConcentration !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  HBsAg concentration
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hbsagConcentration,
                                    data.hbsagConcentrationNormal,
                                    data.hbsagConcentrationUnit,
                                  )}
                                >
                                  {data.hbsagConcentration}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hbsagConcentrationNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hbsagConcentrationUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.hbsagMethodology !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>HBsAg methodology</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hbsagMethodology,
                                    data.hbsagMethodologyNormal,
                                    data.hbsagMethodologyUnit,
                                  )}
                                >
                                  {data.hbsagMethodology}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hbsagMethodologyNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hbsagMethodologyUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.coi !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>HCV Ab COI</BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.coi,
                                    data.coiNormal,
                                    data.coiUnit,
                                  )}
                                >
                                  {data.coi}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.coiNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.coiUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                            {data.hcvAbMethodology !== "-" && (
                              <BloodTestRow>
                                <BloodTestCell>
                                  HCV Ab methodology
                                </BloodTestCell>
                                <BloodTestCell
                                  center
                                  abnormal={isAbnormal(
                                    data.hcvAbMethodology,
                                    data.hcvAbMethodologyNormal,
                                    data.hcvAbMethodologyUnit,
                                  )}
                                >
                                  {data.hcvAbMethodology}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hcvAbMethodologyNormal}
                                </BloodTestCell>
                                <BloodTestCell center>
                                  {data.hcvAbMethodologyUnit}
                                </BloodTestCell>
                              </BloodTestRow>
                            )}
                          </BloodTestContent>
                        </BloodTestTableWrapper>
                      )}
                    </>
                  )}

                  {/* 12. การตรวจเลือดแฝงในอุจจาระ (Occult Blood) */}
                  {(data.hemoglobinDetectionStool !== "-" ||
                    data.hemoglobinDetectionStoolMethodology !== "-" ||
                    data.hemoglobinDetectionStoolReagentKits !== "-" ||
                    data.hemoglobinDetectionStoolLot !== "-" ||
                    data.hemoglobinDetectionStoolExpiredDate !== "-" ||
                    data.hemoglobinDetectionStoolSensitivity !== "-" ||
                    data.hemoglobinDetectionStoolSpecificity !== "-") && (
                    <BloodTestTableWrapper>
                      <BloodTestHeader>
                        การตรวจเลือดแฝงในอุจจาระ
                      </BloodTestHeader>
                      <BloodTestContent>
                        <BloodTestRow>
                          <BloodTestCell header>รายการ</BloodTestCell>
                          <BloodTestCell header center>
                            ผลตรวจ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            ค่าปกติ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            หน่วย
                          </BloodTestCell>
                        </BloodTestRow>
                        {data.hemoglobinDetectionStool !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>
                              Hemoglobin detection stool
                            </BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.hemoglobinDetectionStool,
                                data.hemoglobinDetectionStoolNormal,
                                data.hemoglobinDetectionStoolUnit,
                              )}
                            >
                              {data.hemoglobinDetectionStool}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.hemoglobinDetectionStoolMethodology !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>
                              Hemoglobin detection stool methodology
                            </BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.hemoglobinDetectionStoolMethodology,
                                data.hemoglobinDetectionStoolMethodologyNormal,
                                data.hemoglobinDetectionStoolMethodologyUnit,
                              )}
                            >
                              {data.hemoglobinDetectionStoolMethodology}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolMethodologyNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolMethodologyUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.hemoglobinDetectionStoolReagentKits !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>
                              Hemoglobin detection stool reagent kits
                            </BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.hemoglobinDetectionStoolReagentKits,
                                data.hemoglobinDetectionStoolReagentKitsNormal,
                                data.hemoglobinDetectionStoolReagentKitsUnit,
                              )}
                            >
                              {data.hemoglobinDetectionStoolReagentKits}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolReagentKitsNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolReagentKitsUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.hemoglobinDetectionStoolLot !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>
                              Hemoglobin detection stool lot
                            </BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.hemoglobinDetectionStoolLot,
                                data.hemoglobinDetectionStoolLotNormal,
                                data.hemoglobinDetectionStoolLotUnit,
                              )}
                            >
                              {data.hemoglobinDetectionStoolLot}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolLotNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolLotUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.hemoglobinDetectionStoolExpiredDate !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>
                              Hemoglobin detection stool expired date
                            </BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.hemoglobinDetectionStoolExpiredDate,
                                data.hemoglobinDetectionStoolExpiredDateNormal,
                                data.hemoglobinDetectionStoolExpiredDateUnit,
                              )}
                            >
                              {data.hemoglobinDetectionStoolExpiredDate}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolExpiredDateNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolExpiredDateUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.hemoglobinDetectionStoolSensitivity !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>
                              Hemoglobin detection stool sensitivity
                            </BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.hemoglobinDetectionStoolSensitivity,
                                data.hemoglobinDetectionStoolSensitivityNormal,
                                data.hemoglobinDetectionStoolSensitivityUnit,
                              )}
                            >
                              {data.hemoglobinDetectionStoolSensitivity}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolSensitivityNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolSensitivityUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.hemoglobinDetectionStoolSpecificity !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>
                              Hemoglobin detection stool specificity
                            </BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.hemoglobinDetectionStoolSpecificity,
                                data.hemoglobinDetectionStoolSpecificityNormal,
                                data.hemoglobinDetectionStoolSpecificityUnit,
                              )}
                            >
                              {data.hemoglobinDetectionStoolSpecificity}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolSpecificityNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.hemoglobinDetectionStoolSpecificityUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                      </BloodTestContent>
                    </BloodTestTableWrapper>
                  )}

                  {/* 13. เกลือแร่ในเลือด (Electrolytes) */}
                  {(data.sodium !== "-" ||
                    data.potassium !== "-" ||
                    data.chloride !== "-" ||
                    data.bicarbonate !== "-" ||
                    data.totalCalcium !== "-" ||
                    data.inorganicPhosphate !== "-" ||
                    data.magnesium !== "-") && (
                    <BloodTestTableWrapper>
                      <BloodTestHeader>เกลือแร่ในเลือด</BloodTestHeader>
                      <BloodTestContent>
                        <BloodTestRow>
                          <BloodTestCell header>รายการ</BloodTestCell>
                          <BloodTestCell header center>
                            ผลตรวจ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            ค่าปกติ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            หน่วย
                          </BloodTestCell>
                        </BloodTestRow>
                        {data.sodium !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Sodium</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.sodium,
                                data.sodiumNormal,
                                data.sodiumUnit,
                              )}
                            >
                              {data.sodium}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.sodiumNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.sodiumUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.potassium !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Potassium</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.potassium,
                                data.potassiumNormal,
                                data.potassiumUnit,
                              )}
                            >
                              {data.potassium}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.potassiumNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.potassiumUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.chloride !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Chloride</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.chloride,
                                data.chlorideNormal,
                                data.chlorideUnit,
                              )}
                            >
                              {data.chloride}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.chlorideNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.chlorideUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.bicarbonate !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Bicarbonate</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.bicarbonate,
                                data.bicarbonateNormal,
                                data.bicarbonateUnit,
                              )}
                            >
                              {data.bicarbonate}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.bicarbonateNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.bicarbonateUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.totalCalcium !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Total Calcium</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.totalCalcium,
                                data.totalCalciumNormal,
                                data.totalCalciumUnit,
                              )}
                            >
                              {data.totalCalcium}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.totalCalciumNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.totalCalciumUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.inorganicPhosphate !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Inorganic phosphate</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.inorganicPhosphate,
                                data.inorganicPhosphateNormal,
                                data.inorganicPhosphateUnit,
                              )}
                            >
                              {data.inorganicPhosphate}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.inorganicPhosphateNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.inorganicPhosphateUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.magnesium !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Magnesium</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.magnesium,
                                data.magnesiumNormal,
                                data.magnesiumUnit,
                              )}
                            >
                              {data.magnesium}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.magnesiumNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.magnesiumUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                      </BloodTestContent>
                    </BloodTestTableWrapper>
                  )}

                  {/* 14. ค่าบ่งชี้มะเร็ง (Tumor Markers) */}
                  {(data.afp !== "-" ||
                    data.psa !== "-" ||
                    data.cea !== "-" ||
                    data.ca153 !== "-" ||
                    data.ca199 !== "-" ||
                    data.ca125 !== "-") && (
                    <BloodTestTableWrapper>
                      <BloodTestHeader>ค่าบ่งชี้มะเร็ง</BloodTestHeader>
                      <BloodTestContent>
                        <BloodTestRow>
                          <BloodTestCell header>รายการ</BloodTestCell>
                          <BloodTestCell header center>
                            ผลตรวจ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            ค่าปกติ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            หน่วย
                          </BloodTestCell>
                        </BloodTestRow>
                        {data.afp !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>AFP</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.afp,
                                data.afpNormal,
                                data.afpUnit,
                              )}
                            >
                              {data.afp}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.afpNormal}
                            </BloodTestCell>
                            <BloodTestCell center>{data.afpUnit}</BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.psa !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>PSA</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.psa,
                                data.psaNormal,
                                data.psaUnit,
                              )}
                            >
                              {data.psa}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.psaNormal}
                            </BloodTestCell>
                            <BloodTestCell center>{data.psaUnit}</BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.cea !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>CEA</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.cea,
                                data.ceaNormal,
                                data.ceaUnit,
                              )}
                            >
                              {data.cea}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.ceaNormal}
                            </BloodTestCell>
                            <BloodTestCell center>{data.ceaUnit}</BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.ca153 !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>CA153</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.ca153,
                                data.ca153Normal,
                                data.ca153Unit,
                              )}
                            >
                              {data.ca153}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.ca153Normal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.ca153Unit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.ca199 !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>CA19-9</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.ca199,
                                data.ca199Normal,
                                data.ca199Unit,
                              )}
                            >
                              {data.ca199}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.ca199Normal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.ca199Unit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.ca125 !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>CA125</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.ca125,
                                data.ca125Normal,
                                data.ca125Unit,
                              )}
                            >
                              {data.ca125}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.ca125Normal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.ca125Unit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                      </BloodTestContent>
                    </BloodTestTableWrapper>
                  )}

                  {/* 15. ฮอร์โมนไทรอยด์ (Thyroid Hormones) */}
                  {(data.freeT3 !== "-" ||
                    data.freeT4 !== "-" ||
                    data.tsh !== "-") && (
                    <BloodTestTableWrapper>
                      <BloodTestHeader>ฮอร์โมนไทรอยด์</BloodTestHeader>
                      <BloodTestContent>
                        <BloodTestRow>
                          <BloodTestCell header>รายการ</BloodTestCell>
                          <BloodTestCell header center>
                            ผลตรวจ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            ค่าปกติ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            หน่วย
                          </BloodTestCell>
                        </BloodTestRow>
                        {data.freeT3 !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Free T3</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.freeT3,
                                data.freeT3Normal,
                                data.freeT3Unit,
                              )}
                            >
                              {data.freeT3}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.freeT3Normal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.freeT3Unit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.freeT4 !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Free T4</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.freeT4,
                                data.freeT4Normal,
                                data.freeT4Unit,
                              )}
                            >
                              {data.freeT4}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.freeT4Normal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.freeT4Unit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                        {data.tsh !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>TSH</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.tsh,
                                data.tshNormal,
                                data.tshUnit,
                              )}
                            >
                              {data.tsh}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.tshNormal}
                            </BloodTestCell>
                            <BloodTestCell center>{data.tshUnit}</BloodTestCell>
                          </BloodTestRow>
                        )}
                      </BloodTestContent>
                    </BloodTestTableWrapper>
                  )}

                  {/* 17. การสัมผัสสารพิษกลุ่มยาฆ่าแมลง (Pesticide Exposure) */}
                  {data.cholinesterase !== "-" && (
                    <BloodTestTableWrapper>
                      <BloodTestHeader>
                        การสัมผัสสารพิษกลุ่มยาฆ่าแมลง
                      </BloodTestHeader>
                      <BloodTestContent>
                        <BloodTestRow>
                          <BloodTestCell header>รายการ</BloodTestCell>
                          <BloodTestCell header center>
                            ผลตรวจ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            ค่าปกติ
                          </BloodTestCell>
                          <BloodTestCell header center>
                            หน่วย
                          </BloodTestCell>
                        </BloodTestRow>
                        {data.cholinesterase !== "-" && (
                          <BloodTestRow>
                            <BloodTestCell>Cholinesterase</BloodTestCell>
                            <BloodTestCell
                              center
                              abnormal={isAbnormal(
                                data.cholinesterase,
                                data.cholinesteraseNormal,
                                data.cholinesteraseUnit,
                              )}
                            >
                              {data.cholinesterase}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.cholinesteraseNormal}
                            </BloodTestCell>
                            <BloodTestCell center>
                              {data.cholinesteraseUnit}
                            </BloodTestCell>
                          </BloodTestRow>
                        )}
                      </BloodTestContent>
                    </BloodTestTableWrapper>
                  )}
                </ReportContainer>

                {/* เพิ่ม PageBreak ระหว่าง records (ยกเว้นคนสุดท้าย) */}
                {recordIndex < recordsArray.length - 1 && <PageBreak />}
              </React.Fragment>
            );
          })}
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default HealthReportModal;
